import { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import emailjs from '@emailjs/browser';

// Creates a Supabase auth user without touching the admin's active session
const createAuthUser = async (email, password) => {
  const temp = createClient(
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY,
    { auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } }
  );
  return temp.auth.signUp({ email, password });
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ADMIN_EMAIL = 'admin@ultimateprintingco.com';
const MATERIALS   = ['Substance 2755'];
const LAMINATES   = ['Gloss','Matte'];
const SIZES       = ['Full Wrap','Partial Wrap','Hood Only','Roof Only','Doors Only','Custom'];
const ALL_STATUSES = ['Awaiting Files','Pending Review','Proof Sent','Quote Sent','Proof Approved','In Production','Ready to Ship','Completed','On Hold'];

const STATUS_CFG = {
  'Awaiting Files': { color:'#f59e0b', bg:'rgba(245,158,11,0.1)',   icon:'⏳' },
  'Pending Review': { color:'#009fff', bg:'rgba(0,159,255,0.1)',    icon:'👁'  },
  'Proof Sent':     { color:'#8b5cf6', bg:'rgba(139,92,246,0.1)',   icon:'📧' },
  'Quote Sent':     { color:'#f59e0b', bg:'rgba(245,158,11,0.1)',   icon:'💰' },
  'Proof Approved': { color:'#06b6d4', bg:'rgba(6,182,212,0.1)',    icon:'✅' },
  'In Production':  { color:'#10b981', bg:'rgba(16,185,129,0.1)',   icon:'⚙️'  },
  'Ready to Ship':  { color:'#f97316', bg:'rgba(249,115,22,0.1)',   icon:'📦' },
  'Completed':      { color:'#6b7280', bg:'rgba(107,114,128,0.1)',  icon:'✓'  },
  'On Hold':        { color:'#ef4444', bg:'rgba(239,68,68,0.1)',    icon:'⛔' },
};

const genOrderId = () => `ORD-${Date.now().toString(36).toUpperCase()}`;

// ─── DESIGN SYSTEM CSS ────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:          #09090E;
    --bg-card:     #101017;
    --bg-elevated: #17171F;
    --bg-input:    #07070C;
    --bg-sidebar:  #0C0C14;
    --border:      #1D1D2C;
    --border-sub:  #141420;
    --accent:      #009fff;
    --accent-dim:  rgba(0,159,255,0.07);
    --accent-glow: rgba(0,159,255,0.18);
    --accent-mid:  rgba(0,159,255,0.12);
    --text:        #E2E6F0;
    --text-2:      #4E5A70;
    --text-3:      #1E2535;
    --success:     #10b981;
    --warn:        #f59e0b;
    --danger:      #ef4444;
    --r:           8px;
    --r-sm:        5px;
    --sidebar-w:   220px;
  }

  body { background: var(--bg); font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; color: var(--text); -webkit-font-smoothing: antialiased; }
  input, select, textarea { font-family: 'Inter', -apple-system, sans-serif; }
  ::-webkit-scrollbar { width: 3px; height: 3px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #1D2538; border-radius: 2px; }

  .bc   { font-family: 'Space Grotesk', sans-serif; }
  .mono { font-family: 'JetBrains Mono', 'Fira Code', monospace; font-size: 0.92em; }

  /* ── Sidebar ── */
  .sidebar {
    position: fixed; left: 0; top: 0; bottom: 0; width: var(--sidebar-w);
    background: var(--bg-sidebar); border-right: 1px solid var(--border);
    display: flex; flex-direction: column; z-index: 100;
  }
  .sidebar::after {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0,159,255,0.35), transparent);
  }
  .sidebar-logo {
    padding: 20px 18px 16px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 10px; flex-shrink: 0;
  }
  .sidebar-badge {
    font-family: 'Space Grotesk', sans-serif; font-size: 8px; font-weight: 700;
    letter-spacing: 1.5px; text-transform: uppercase; color: var(--accent);
    background: rgba(0,159,255,0.1); border: 1px solid rgba(0,159,255,0.18);
    padding: 3px 6px; border-radius: 3px;
  }
  .sidebar-section {
    padding: 16px 10px 4px; font-size: 9px; font-weight: 600; letter-spacing: 2px;
    text-transform: uppercase; color: var(--text-3); padding-left: 14px;
  }
  .sidebar-nav { flex: 1; padding: 10px 8px; overflow-y: auto; display: flex; flex-direction: column; gap: 1px; }
  .sidebar-item {
    display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: 6px;
    border: 1px solid transparent; cursor: pointer; font-family: 'Inter', sans-serif;
    font-size: 13px; font-weight: 500; color: var(--text-2); background: transparent;
    transition: all .13s; text-align: left; width: 100%;
  }
  .sidebar-item:hover { background: var(--bg-elevated); color: var(--text); border-color: var(--border); }
  .sidebar-item.active { background: rgba(0,159,255,0.09); color: var(--accent); border-color: rgba(0,159,255,0.18); }
  .sidebar-icon { width: 15px; height: 15px; flex-shrink: 0; opacity: .55; transition: opacity .13s; }
  .sidebar-item:hover .sidebar-icon { opacity: .75; }
  .sidebar-item.active .sidebar-icon { opacity: 1; }
  .sidebar-footer { padding: 12px 10px; border-top: 1px solid var(--border); flex-shrink: 0; }
  .sidebar-user { padding: 0 4px 10px; }
  .sidebar-user-name { font-size: 12px; font-weight: 600; color: var(--text); margin-bottom: 1px; }
  .sidebar-user-email { font-size: 10px; color: var(--text-2); letter-spacing: .2px; }
  .sidebar-footer-row { display: flex; gap: 6px; }
  .sidebar-theme-btn {
    flex-shrink: 0; width: 34px; background: var(--bg-elevated); border: 1px solid var(--border);
    color: var(--text-2); border-radius: var(--r-sm); cursor: pointer; font-size: 13px;
    transition: all .13s; display: flex; align-items: center; justify-content: center;
  }
  .sidebar-theme-btn:hover { border-color: rgba(0,159,255,0.4); color: var(--text); }
  .sidebar-signout {
    flex: 1; background: var(--bg-elevated); border: 1px solid var(--border);
    color: var(--text-2); border-radius: var(--r-sm); padding: 7px 10px;
    cursor: pointer; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500;
    transition: all .13s; text-align: center;
  }
  .sidebar-signout:hover { border-color: rgba(239,68,68,0.35); color: var(--danger); }

  /* ── Main layout ── */
  .main-layout { display: flex; min-height: 100vh; }
  .main-content { flex: 1; margin-left: var(--sidebar-w); min-height: 100vh; }
  .main-inner { max-width: 1120px; margin: 0 auto; padding: 36px 32px; }

  /* ── Buttons ── */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    border: none; cursor: pointer; font-family: 'Inter', sans-serif; font-weight: 600;
    letter-spacing: 0; transition: all .15s; white-space: nowrap;
  }
  .btn-primary {
    background: var(--accent); color: #000; padding: 10px 22px; font-size: 13px;
    border-radius: var(--r-sm);
  }
  .btn-primary:hover { background: #26AAFF; box-shadow: 0 0 0 3px rgba(0,159,255,0.18); }
  .btn-primary:disabled { opacity: .3; cursor: not-allowed; box-shadow: none; }
  .btn-ghost {
    background: transparent; border: 1px solid var(--border); color: var(--text-2);
    padding: 9px 18px; font-size: 12px; border-radius: var(--r-sm);
  }
  .btn-ghost:hover { border-color: rgba(0,159,255,0.35); color: var(--text); background: var(--accent-dim); }
  .btn-sm { padding: 5px 12px !important; font-size: 11px !important; }
  .btn-danger {
    background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
    color: var(--danger); padding: 8px 14px; font-size: 11px; border-radius: var(--r-sm);
  }
  .btn-danger:hover { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.4); }
  .btn-danger-confirm {
    background: var(--danger); color: #fff; border: none; padding: 8px 14px;
    font-size: 11px; border-radius: var(--r-sm);
  }

  /* ── Cards ── */
  .card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--r); overflow: hidden; }

  /* ── Form ── */
  .flabel {
    color: var(--text-2); font-size: 10px; font-weight: 600; letter-spacing: 1.5px;
    text-transform: uppercase; margin-bottom: 6px; display: block;
  }
  .finput {
    width: 100%; background: var(--bg-input); border: 1px solid var(--border);
    color: var(--text); padding: 10px 13px; font-size: 14px;
    border-radius: var(--r-sm); outline: none; transition: border-color .15s, box-shadow .15s;
  }
  .finput:focus { border-color: rgba(0,159,255,0.5); box-shadow: 0 0 0 3px rgba(0,159,255,0.08); }
  .finput::placeholder { color: var(--text-3); }
  .fselect {
    width: 100%; background: var(--bg-input); border: 1px solid var(--border);
    color: var(--text); padding: 10px 13px; font-size: 14px;
    border-radius: var(--r-sm); outline: none; appearance: none; cursor: pointer;
    transition: border-color .15s;
  }
  .fselect:focus { border-color: rgba(0,159,255,0.5); }

  /* ── Overlay / Modal ── */
  .overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.75); backdrop-filter: blur(10px);
    z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .modal {
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--r);
    width: 100%; max-width: 680px; max-height: 92vh; overflow-y: auto;
    box-shadow: 0 32px 96px rgba(0,0,0,.8);
  }
  .modal-wide { max-width: 940px; }

  /* ── Nav tabs (inside modals) ── */
  .nav-tab {
    background: none; border: none; border-bottom: 2px solid transparent;
    color: var(--text-2); padding: 10px 16px; font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase;
    cursor: pointer; transition: all .15s;
  }
  .nav-tab:hover { color: var(--text); }
  .nav-tab.active { color: var(--accent); border-bottom-color: var(--accent); }

  /* ── Order row ── */
  .orow {
    border-bottom: 1px solid var(--border-sub); padding: 14px 20px;
    cursor: pointer; transition: background .12s;
  }
  .orow:hover { background: var(--bg-elevated); }
  .orow:last-child { border-bottom: none; }

  /* ── Status pill ── */
  .pill {
    display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px;
    font-family: 'Inter', sans-serif; font-weight: 600; font-size: 10px;
    letter-spacing: .5px; text-transform: uppercase; border-radius: 4px;
  }

  /* ── Drop zone ── */
  .drop-zone {
    border: 1px dashed var(--border); background: var(--bg-input);
    border-radius: var(--r); padding: 40px; text-align: center; cursor: pointer; transition: all .15s;
  }
  .drop-zone.over { border-color: var(--accent); background: var(--accent-dim); }

  /* ── Toast ── */
  .notif {
    position: fixed; top: 14px; right: 14px; z-index: 9999;
    padding: 11px 16px; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
    animation: slideIn .2s ease; max-width: 340px;
    box-shadow: 0 8px 40px rgba(0,0,0,.7); border-radius: var(--r-sm);
    border: 1px solid rgba(255,255,255,0.07);
  }
  @keyframes slideIn { from { transform: translateX(100px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

  /* ── Stat cards ── */
  .stat {
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--r); padding: 20px;
    transition: border-color .15s;
  }
  .stat:hover { border-color: rgba(0,159,255,0.2); }

  /* ── Table ── */
  table { width: 100%; border-collapse: collapse; }
  th {
    color: var(--text-2); font-size: 10px; font-weight: 600; letter-spacing: 1.5px;
    text-transform: uppercase; padding: 11px 18px; text-align: left;
    border-bottom: 1px solid var(--border); font-family: 'Inter', sans-serif;
  }
  td { padding: 13px 18px; border-bottom: 1px solid var(--border-sub); font-size: 13px; color: var(--text); vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  tr.click:hover td { background: var(--bg-elevated); cursor: pointer; }

  /* ── File tag ── */
  .tag {
    background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-2);
    padding: 5px 10px; font-size: 11px; border-radius: var(--r-sm);
    display: inline-flex; align-items: center; gap: 6px; margin: 0 5px 5px 0;
    transition: border-color .12s;
  }
  .tag:hover { border-color: rgba(0,159,255,0.35); color: var(--text); }

  /* ── Progress ── */
  .progress-bar { height: 2px; background: var(--accent); transition: width .3s; border-radius: 2px; }

  /* ── Divider ── */
  .divider { height: 1px; background: var(--border); margin: 20px 0; }

  /* ── Section label ── */
  .section-label {
    font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase;
    color: var(--text-2); margin-bottom: 12px;
  }

  /* ── Login ── */
  .login-grid {
    position: absolute; inset: 0; pointer-events: none; overflow: hidden;
    background-image:
      linear-gradient(rgba(0,159,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,159,255,0.03) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 70% 80% at 50% 50%, black 30%, transparent 100%);
  }
  .login-glow {
    position: absolute; top: 15%; left: 50%; transform: translate(-50%, -50%);
    width: 700px; height: 500px; pointer-events: none;
    background: radial-gradient(ellipse at center, rgba(0,159,255,0.06) 0%, transparent 65%);
  }

  /* ── Light mode ── */
  body[data-theme="light"] {
    --bg: #F1F3F8; --bg-card: #FFFFFF; --bg-elevated: #E8ECF4;
    --bg-input: #F7F9FC; --bg-sidebar: #0C0C14;
    --border: #DDE2EE; --border-sub: #E8ECF5;
    --text: #0D0F1C; --text-2: #637288; --text-3: #B4C0D4;
  }
  body[data-theme="light"] .sidebar { background: var(--bg-sidebar); }
  body[data-theme="light"] .login-grid {
    background-image:
      linear-gradient(rgba(0,0,0,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,0,0,0.035) 1px, transparent 1px);
  }

  /* ── Mobile bar ── */
  .mobile-bar {
    display: none; position: fixed; top: 0; left: 0; right: 0; height: 52px; z-index: 90;
    background: var(--bg-sidebar); border-bottom: 1px solid var(--border);
    align-items: center; justify-content: space-between; padding: 0 14px;
  }
  .hamburger {
    background: none; border: none; color: var(--text-2); cursor: pointer;
    padding: 8px 6px; display: flex; flex-direction: column; gap: 4px; border-radius: var(--r-sm);
  }
  .hamburger:hover { background: var(--bg-elevated); color: var(--text); }
  .hamburger span { display: block; width: 18px; height: 1.5px; background: currentColor; border-radius: 1px; }

  /* ── Sidebar overlay (mobile) ── */
  .sidebar-overlay {
    display: none; position: fixed; inset: 0; z-index: 99;
    background: rgba(0,0,0,0.55); backdrop-filter: blur(4px);
  }

  /* ── Responsive 2-col grid ── */
  .grid-2col { display: grid; gap: 14px; margin-bottom: 14px; grid-template-columns: 1fr 1fr; }

  /* ── Mobile breakpoint ── */
  @media (max-width: 768px) {
    .mobile-bar { display: flex; }

    .sidebar {
      transform: translateX(-100%);
      transition: transform .25s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 100;
    }
    .sidebar.open { transform: translateX(0); box-shadow: 8px 0 40px rgba(0,0,0,0.6); }

    .sidebar-overlay { display: block; opacity: 0; pointer-events: none; transition: opacity .25s; }
    .sidebar-overlay.open { opacity: 1; pointer-events: auto; }

    .main-content { margin-left: 0; padding-top: 52px; }
    .main-inner { padding: 20px 16px; }

    .overlay { padding: 0; align-items: flex-end; }
    .modal { max-width: 100%; border-radius: var(--r) var(--r) 0 0; max-height: 94vh; }
    .modal-wide { max-width: 100%; }

    .card { overflow-x: auto; }
    table { min-width: 520px; }

    .grid-2col { grid-template-columns: 1fr; }

    .drop-zone { padding: 28px 16px; }
    .orow { padding: 12px 16px; }
    th, td { padding: 11px 14px; }
  }
`;

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const c = STATUS_CFG[status] || { color:'#4a6480', bg:'rgba(74,100,128,.1)', icon:'?' };
  return <span className="pill" style={{ background:c.bg, color:c.color }}>{c.icon} {status}</span>;
};

const Notif = ({ n }) => {
  if (!n) return null;
  const bg = n.type === 'error' ? '#b91c1c' : n.type === 'warn' ? '#78350f' : '#065f46';
  return (
    <div className="notif" style={{ background:bg, color:'#fff' }}>
      {n.type === 'error' ? '✕ ' : n.type === 'warn' ? '⚠ ' : '✓ '}{n.msg}
    </div>
  );
};

const FInput = ({ label, ...p }) => (
  <div style={{ marginBottom:16 }}>
    {label && <label className="flabel">{label}</label>}
    <input className="finput" {...p} />
  </div>
);

const FSelect = ({ label, opts, ...p }) => (
  <div style={{ marginBottom:16 }}>
    {label && <label className="flabel">{label}</label>}
    <select className="fselect" {...p}>
      {opts.map(o => typeof o === 'string'
        ? <option key={o}>{o}</option>
        : <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

const Logo = ({ height = 36 }) => (
  <img src="/logo.svg" height={height} alt="Ultimate Printing Co" style={{ display:'block' }} />
);

const Icons = {
  dashboard: <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor"><rect x="1" y="1" width="5.5" height="5.5" rx="1.5"/><rect x="8.5" y="1" width="5.5" height="5.5" rx="1.5"/><rect x="1" y="8.5" width="5.5" height="5.5" rx="1.5"/><rect x="8.5" y="8.5" width="5.5" height="5.5" rx="1.5"/></svg>,
  orders:    <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 3.5h11M2 7.5h11M2 11.5h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  companies: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="5" width="12" height="9" rx="1" stroke="currentColor" strokeWidth="1.4"/><path d="M5 5V3.5a2.5 2.5 0 015 0V5" stroke="currentColor" strokeWidth="1.4"/><circle cx="7.5" cy="10" r="1.3" fill="currentColor"/></svg>,
  products:  <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 1.5l5.5 3.1v6.3L7.5 14 2 10.9V4.6l5.5-3.1z" stroke="currentColor" strokeWidth="1.4"/><path d="M7.5 1.5v12.5M2 4.6l5.5 3.2 5.5-3.2" stroke="currentColor" strokeWidth="1.2" opacity=".45"/></svg>,
  calendar:  <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1.5" y="2.5" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><path d="M5 1v3M10 1v3M1.5 6.5h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  account:   <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/><path d="M2 13.5c0-3.04 2.46-4.5 5.5-4.5s5.5 1.46 5.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
};

const Sidebar = ({ items, active, onNav, onLogout, badge, theme, onThemeToggle, userEmail, open, onClose }) => (
  <>
    <div className={`sidebar-overlay${open ? ' open' : ''}`} onClick={onClose} />
    <div className={`sidebar${open ? ' open' : ''}`}>
      <div className="sidebar-logo">
        <Logo height={28} />
        {badge && <span className="sidebar-badge">{badge}</span>}
      </div>
      <nav className="sidebar-nav">
        {items.map(([v, l, icon]) => (
          <button key={v} className={`sidebar-item${active === v ? ' active' : ''}`} onClick={() => { onNav(v); onClose?.(); }}>
            <span className="sidebar-icon">{icon}</span>
            {l}
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        {userEmail && (
          <div className="sidebar-user">
            <div className="sidebar-user-name">{userEmail.split('@')[0]}</div>
            <div className="sidebar-user-email">{userEmail}</div>
          </div>
        )}
        <div className="sidebar-footer-row">
          {onThemeToggle && (
            <button className="sidebar-theme-btn" onClick={onThemeToggle} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
              {theme === 'dark' ? '○' : '●'}
            </button>
          )}
          <button className="sidebar-signout" onClick={onLogout}>Sign out</button>
        </div>
      </div>
    </div>
  </>
);

// ─── ORDER DETAIL MODAL ───────────────────────────────────────────────────────
function OrderModal({ order, company, onClose, onUpdate, isAdmin, senderEmail, senderRole }) {
  const [tab,        setTab]        = useState('details');
  const [status,     setStatus]     = useState(order.status);
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || '');
  const [tracking,   setTracking]   = useState(order.tracking_number || '');
  const [saving,     setSaving]     = useState(false);
  const [payments,   setPayments]   = useState(order.payments || []);
  const [files,      setFiles]      = useState(order.files || []);
  const [messages,   setMessages]   = useState([]);
  const [msgText,    setMsgText]    = useState('');
  const [sending,       setSending]       = useState(false);
  const [msgErr,        setMsgErr]        = useState('');
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadErr,     setUploadErr]     = useState('');
  const addFileRef         = useRef();
  const msgEndRef          = useRef();
  const proofRef           = useRef();
  const [deletingFile,     setDeletingFile]     = useState(null);
  const [proofUploading,   setProofUploading]   = useState(false);
  const [showChangesInput, setShowChangesInput] = useState(false);
  const [changesNote,      setChangesNote]      = useState('');

  const loadMessages = useCallback(async () => {
    const { data } = await supabase.from('messages').select('*').eq('order_id', order.id).order('created_at', { ascending:true });
    setMessages(data || []);
  }, [order.id]);

  // Initial load + real-time subscriptions for messages and files
  useEffect(() => {
    loadMessages();

    const msgChan = supabase
      .channel(`order-msgs-${order.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `order_id=eq.${order.id}` },
        payload => setMessages(prev =>
          prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new]
        )
      )
      .subscribe();

    const fileChan = supabase
      .channel(`order-files-${order.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'order_files', filter: `order_id=eq.${order.id}` },
        payload => setFiles(prev =>
          prev.some(f => f.id === payload.new.id) ? prev : [...prev, payload.new]
        )
      )
      .subscribe();

    return () => {
      supabase.removeChannel(msgChan);
      supabase.removeChannel(fileChan);
    };
  }, [order.id, loadMessages]);

  useEffect(() => {
    if (tab === 'messages') msgEndRef.current?.scrollIntoView({ behavior:'smooth' });
  }, [tab, messages]);

  const sendMessage = async () => {
    if (!msgText.trim() || sending) return;
    setSending(true);
    setMsgErr('');
    try {
      const { error } = await supabase.from('messages').insert({
        order_id:     order.id,
        company_id:   order.company_id,
        sender_email: senderEmail,
        sender_role:  senderRole,
        message:      msgText.trim(),
      });
      if (error) throw error;
      setMsgText('');
      // Real-time subscription appends the new message — no manual reload needed
    } catch (e) {
      setMsgErr(e.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const uploadAdditionalFiles = async (fl) => {
    const allowed = ['.pdf','.eps','.jpg','.jpeg','.png','.ai','.svg'];
    const valid = Array.from(fl).filter(f => allowed.some(ext => f.name.toLowerCase().endsWith(ext)));
    if (!valid.length) return;
    setFileUploading(true);
    setUploadErr('');
    try {
      const records = [];
      for (const f of valid) {
        const path = `${order.company_id}/${order.id}/${Date.now()}-${f.name}`;
        const { error: storErr } = await supabase.storage.from('wrap-files').upload(path, f);
        if (storErr) throw storErr;
        records.push({ order_id:order.id, file_name:f.name, file_path:path, file_size:f.size });
      }
      if (records.length > 0) {
        const { data:inserted, error:dbErr } = await supabase.from('order_files').insert(records).select();
        if (dbErr) throw dbErr;
        if (inserted) setFiles(p => [...p, ...inserted]);
      }
    } catch (e) {
      setUploadErr(e.message || 'Upload failed. Check storage permissions.');
    } finally {
      setFileUploading(false);
    }
  };

  const togglePaymentStatus = async (payment) => {
    const newStatus = payment.status === 'confirmed' ? 'pending' : 'confirmed';
    const { error } = await supabase.from('payments').update({ status: newStatus }).eq('id', payment.id);
    if (!error) setPayments(p => p.map(py => py.id === payment.id ? { ...py, status: newStatus } : py));
  };

  const save = async () => {
    setSaving(true);
    await onUpdate({ ...order, status, admin_notes: adminNotes, tracking_number: tracking });
    setSaving(false);
  };

  const downloadUrl = async (file) => {
    const { data } = await supabase.storage.from('wrap-files').createSignedUrl(file.file_path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  const deleteFile = async (file) => {
    try {
      await supabase.storage.from('wrap-files').remove([file.file_path]);
      const { error } = await supabase.from('order_files').delete().eq('id', file.id);
      if (error) throw error;
      setFiles(p => p.filter(f => f.id !== file.id));
      setDeletingFile(null);
    } catch (e) {
      setUploadErr(e.message || 'Delete failed.');
    }
  };

  const uploadProof = async (fl) => {
    const file = fl[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      setUploadErr('Proof must be a PDF file.'); return;
    }
    setProofUploading(true);
    setUploadErr('');
    try {
      const path = `${order.company_id}/${order.id}/proof-${Date.now()}-${file.name}`;
      const { error: storErr } = await supabase.storage.from('wrap-files').upload(path, file);
      if (storErr) throw storErr;
      const { data: inserted, error: dbErr } = await supabase.from('order_files')
        .insert({ order_id: order.id, file_name: file.name, file_path: path, file_size: file.size, file_type: 'proof' })
        .select().single();
      if (dbErr) throw dbErr;
      if (inserted) setFiles(p => [...p, inserted]);
      await onUpdate({ ...order, status: 'Proof Sent', admin_notes: adminNotes, tracking_number: tracking });
      setStatus('Proof Sent');
      try {
        await supabase.functions.invoke('send-status-email', {
          body: { order_id: order.id, new_status: 'Proof Sent', partner_email: company?.email, order_vehicle: order.vehicle },
        });
      } catch {}
    } catch (e) {
      setUploadErr(e.message || 'Proof upload failed.');
    } finally {
      setProofUploading(false);
    }
  };

  const handleProofResponse = async (approve) => {
    const newStatus  = approve ? 'Proof Approved' : 'Awaiting Files';
    const msgContent = approve
      ? 'Proof approved — ready to proceed to production.'
      : `Changes requested: ${changesNote || 'Please review and revise the proof.'}`;
    setSending(true);
    try {
      await supabase.from('messages').insert({
        order_id: order.id, company_id: order.company_id,
        sender_email: senderEmail, sender_role: senderRole, message: msgContent,
      });
      await onUpdate({ ...order, status: newStatus, admin_notes: adminNotes, tracking_number: tracking });
      setShowChangesInput(false);
      setChangesNote('');
      setTab('messages');
    } catch (e) {
      setMsgErr(e.message || 'Failed to send response.');
    } finally {
      setSending(false);
    }
  };

  const fileIcon = (f) => {
    if (f.file_type === 'proof') return '📋';
    if (f.file_name?.toLowerCase().endsWith('.eps')) return '📐';
    if (f.file_name?.toLowerCase().match(/\.(jpg|jpeg|png|svg)$/)) return '🖼';
    return '📄';
  };

  const sc = STATUS_CFG[order.status] || {};

  return (
    <div className="overlay" onClick={onClose}>
      <div className={`modal ${isAdmin ? 'modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:'22px 26px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', background:'rgba(0,0,0,.2)' }}>
          <div>
            <div className="bc" style={{ fontSize:24, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff', lineHeight:1.1 }}>{order.vehicle}</div>
            <div style={{ color:'var(--text-2)', fontSize:12, marginTop:5 }}>
              {order.id}{company ? <> · <span style={{ color:'var(--accent)' }}>{company.company}</span></> : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:22, cursor:'pointer', lineHeight:1, padding:'2px 6px', borderRadius:4 }}>×</button>
        </div>

        {/* Tab nav */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)', padding:'0 26px', background:'rgba(0,0,0,.1)' }}>
          {[
            ['details','Details'],
            ['files',    files.length > 0 ? `Files (${files.length})` : 'Files'],
            ['messages', messages.length > 0 ? `Messages (${messages.length})` : 'Messages'],
          ].map(([v,l]) => (
            <button key={v} className={`nav-tab ${tab === v ? 'active' : ''}`} onClick={() => setTab(v)}>{l}</button>
          ))}
        </div>

        {tab === 'details' && (
          <div style={{ padding:26, display:'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap:28 }}>
            {/* Quote approval banner for clients */}
            {!isAdmin && order.status === 'Quote Sent' && (
              <div style={{ gridColumn:'1/-1', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'var(--r)', padding:'16px 20px', marginBottom:0, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                <div>
                  <div style={{ color:'#f59e0b', fontWeight:700, fontSize:14, marginBottom:4 }}>💰 Quote Ready for Approval</div>
                  <div style={{ color:'var(--text-2)', fontSize:13 }}>Review the pricing details below and approve to proceed to production.</div>
                </div>
                <button className="btn btn-primary" onClick={() => onUpdate({ ...order, status:'Proof Approved' })}>Approve Quote →</button>
              </div>
            )}
            {/* Left — details */}
            <div>
              <div className="section-label">Order Details</div>
              <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--r)', overflow:'hidden', marginBottom:20 }}>
                {[
                  ['Status',        <StatusPill status={order.status} />],
                  ['Wrap Coverage', order.size],
                  ['Material',      order.material],
                  ['Laminate',      order.laminate],
                  ['Quantity',      `${order.quantity} set(s)`],
                  ['Submitted',     order.created_at?.split('T')[0] || '—'],
                  ['Tracking #',    order.tracking_number || <span style={{ color:'var(--text-3)' }}>—</span>],
                ].map(([k,v], i, arr) => (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'11px 16px', borderBottom: i < arr.length-1 ? '1px solid var(--border-sub)' : 'none', fontSize:13 }}>
                    <span style={{ color:'var(--text-2)' }}>{k}</span>
                    <span style={{ color:'var(--text)', fontWeight:500 }}>{v}</span>
                  </div>
                ))}
              </div>

              {order.notes && (
                <div style={{ marginBottom:20 }}>
                  <div className="flabel">Client Notes</div>
                  <div style={{ background:'var(--bg-elevated)', padding:'12px 15px', color:'var(--text-2)', fontSize:13, lineHeight:'1.7', borderRadius:'var(--r-sm)', borderLeft:'2px solid var(--accent)' }}>
                    {order.notes}
                  </div>
                </div>
              )}

              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setTab('files')}
                style={{ alignSelf:'flex-start' }}
              >
                {files.length > 0
                  ? `📁 ${files.length} file${files.length !== 1 ? 's' : ''} — View Files →`
                  : '📁 No files yet — Upload →'}
              </button>
            </div>

            {/* Right — admin controls or status */}
            <div>
              {isAdmin ? (
                <>
                  <div className="section-label" style={{ color:'var(--warn)' }}>Admin Controls</div>
                  <FSelect label="Update Status" opts={ALL_STATUSES} value={status} onChange={e => setStatus(e.target.value)} />
                  <FInput label="Tracking Number" placeholder="e.g. 1Z999AA10123456784" value={tracking} onChange={e => setTracking(e.target.value)} />
                  <div style={{ marginBottom:16 }}>
                    <label className="flabel">Internal Notes (never shown to client)</label>
                    <textarea className="finput" rows={5} style={{ resize:'vertical' }} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Production notes, file issues, press settings…" />
                  </div>
                  <button className="btn btn-primary" onClick={save} disabled={saving} style={{ width:'100%' }}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <div style={{ marginTop:8 }}>
                    <button className="btn btn-ghost" style={{ width:'100%' }} onClick={() => proofRef.current?.click()} disabled={proofUploading}>
                      {proofUploading ? 'Uploading Proof…' : '📋 Upload Proof PDF'}
                    </button>
                    <input ref={proofRef} type="file" accept=".pdf" style={{ display:'none' }} onChange={e => uploadProof(e.target.files)} />
                  </div>

                  {payments.length > 0 && (
                    <div style={{ marginTop:20 }}>
                      <div className="section-label">Payments</div>
                      {payments.map(py => (
                        <div key={py.id} style={{ background:'var(--bg-elevated)', borderRadius:'var(--r-sm)', padding:'11px 14px', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13 }}>
                          <div>
                            <div style={{ color:'#fff', fontWeight:600 }}>${Number(py.amount).toFixed(2)}</div>
                            <div style={{ color:'var(--text-2)', fontSize:11, marginTop:2 }}>
                              {py.payment_method.replace('_', ' ')}{py.reference_number ? ` · Ref: ${py.reference_number}` : ''}
                            </div>
                          </div>
                          <button
                            onClick={() => togglePaymentStatus(py)}
                            className={`btn btn-sm ${py.status === 'confirmed' ? 'btn-ghost' : 'btn-primary'}`}
                            style={{ fontSize:11 }}
                          >
                            {py.status === 'confirmed' ? '✓ Confirmed' : 'Confirm'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="section-label">Order Status</div>
                  <div style={{ background:sc.bg, border:`1px solid ${sc.color}30`, padding:28, textAlign:'center', borderRadius:'var(--r)', marginBottom:16 }}>
                    <div style={{ fontSize:36, marginBottom:10 }}>{sc.icon}</div>
                    <div className="bc" style={{ fontSize:24, fontWeight:900, color:sc.color, letterSpacing:'1px', textTransform:'uppercase' }}>{order.status}</div>
                    {order.status === 'Proof Sent'    && <div style={{ color:'var(--text-2)', fontSize:12, marginTop:8 }}>Check your email for your digital proof.</div>}
                    {order.status === 'In Production' && <div style={{ color:'var(--text-2)', fontSize:12, marginTop:8 }}>Your wrap is actively being printed.</div>}
                    {order.status === 'Ready to Ship' && <div style={{ color:'var(--text-2)', fontSize:12, marginTop:8 }}>Packaged and awaiting pickup or shipment.</div>}
                  </div>
                  {order.tracking_number && (
                    <div style={{ background:'var(--bg-elevated)', padding:15, borderRadius:'var(--r-sm)', borderLeft:`3px solid var(--success)` }}>
                      <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginBottom:5 }}>Tracking Number</div>
                      <div style={{ color:'var(--success)', fontWeight:600, fontSize:15 }}>{order.tracking_number}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'files' && (
          <div style={{ padding:26 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
              <div className="section-label" style={{ margin:0 }}>Order Files</div>
              <div style={{ display:'flex', gap:8 }}>
                {isAdmin && (
                  <>
                    <button className="btn btn-ghost btn-sm" onClick={() => proofRef.current?.click()} disabled={proofUploading}>
                      {proofUploading ? 'Uploading…' : '📋 Proof'}
                    </button>
                    <input ref={proofRef} type="file" accept=".pdf" style={{ display:'none' }} onChange={e => uploadProof(e.target.files)} />
                  </>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => addFileRef.current?.click()} disabled={fileUploading}>
                  {fileUploading ? 'Uploading…' : '+ Add Files'}
                </button>
                <input ref={addFileRef} type="file" accept=".pdf,.eps,.jpg,.jpeg,.png,.ai,.svg" multiple style={{ display:'none' }} onChange={e => uploadAdditionalFiles(e.target.files)} />
              </div>
            </div>
            {files.length === 0 ? (
              <div style={{ textAlign:'center', color:'var(--text-3)', padding:'60px 0', fontSize:13 }}>⏳ No files uploaded yet</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {files.map(f => {
                  const isProof    = f.file_type === 'proof';
                  const isDeleting = deletingFile === f.id;
                  return (
                    <div key={f.id} style={{ background:'var(--bg-elevated)', border:`1px solid ${isProof ? 'rgba(139,92,246,0.3)' : 'var(--border)'}`, borderRadius:'var(--r-sm)', padding:'12px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                          <span style={{ fontSize:16, flexShrink:0 }}>{fileIcon(f)}</span>
                          <div style={{ minWidth:0 }}>
                            <div style={{ color:'var(--text)', fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.file_name}</div>
                            <div style={{ color:'var(--text-2)', fontSize:11, marginTop:2 }}>
                              {isProof && <span style={{ color:'#8b5cf6', marginRight:6, fontWeight:700 }}>PROOF</span>}
                              {f.file_size ? `${(f.file_size/1024/1024).toFixed(1)} MB` : ''}
                              {f.uploaded_at ? ` · ${f.uploaded_at.split('T')[0]}` : ''}
                            </div>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => downloadUrl(f)}>↓</button>
                          {!isDeleting ? (
                            <button className="btn btn-danger btn-sm" onClick={() => setDeletingFile(f.id)}>Del</button>
                          ) : (
                            <>
                              <button className="btn btn-danger-confirm btn-sm" onClick={() => deleteFile(f)}>Confirm</button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setDeletingFile(null)}>×</button>
                            </>
                          )}
                        </div>
                      </div>
                      {isProof && !isAdmin && order.status === 'Proof Sent' && (
                        <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid var(--border-sub)' }}>
                          {!showChangesInput ? (
                            <div style={{ display:'flex', gap:8 }}>
                              <button className="btn btn-primary btn-sm" style={{ flex:1 }} onClick={() => handleProofResponse(true)} disabled={sending}>
                                ✓ Approve Proof
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ flex:1 }} onClick={() => setShowChangesInput(true)}>
                                Request Changes
                              </button>
                            </div>
                          ) : (
                            <div>
                              <textarea
                                className="finput"
                                rows={2}
                                placeholder="Describe what needs to change…"
                                value={changesNote}
                                onChange={e => setChangesNote(e.target.value)}
                                style={{ marginBottom:8, resize:'vertical' }}
                              />
                              <div style={{ display:'flex', gap:8 }}>
                                <button className="btn btn-primary btn-sm" onClick={() => handleProofResponse(false)} disabled={!changesNote.trim() || sending}>
                                  Send Request
                                </button>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setShowChangesInput(false); setChangesNote(''); }}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {uploadErr && <div style={{ color:'var(--danger)', fontSize:12, marginTop:12 }}>⚠ {uploadErr}</div>}
          </div>
        )}

        {tab === 'messages' && (
          <div style={{ display:'flex', flexDirection:'column', height:480 }}>
            <div style={{ flex:1, overflowY:'auto', padding:'20px 26px' }}>
              {messages.length === 0 && (
                <div style={{ textAlign:'center', color:'var(--text-3)', padding:'60px 0', fontSize:13 }}>
                  No messages yet — start the conversation below.
                </div>
              )}
              {messages.map(m => {
                const isOwn    = m.sender_email === senderEmail;
                const isAdminM = m.sender_role  === 'admin';
                return (
                  <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: isOwn ? 'flex-end' : 'flex-start', marginBottom:16 }}>
                    <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', marginBottom:5 }}>
                      {isAdminM ? '🔧 Ultimate Printing' : `🏢 ${company?.company || m.sender_email}`}
                      {' · '}{new Date(m.created_at).toLocaleDateString()} {new Date(m.created_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
                    </div>
                    <div style={{
                      background:   isAdminM ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                      border:      `1px solid ${isAdminM ? 'rgba(0,159,255,0.2)' : 'var(--border)'}`,
                      borderRadius: 'var(--r-sm)',
                      padding:      '10px 14px',
                      maxWidth:     '80%',
                      fontSize:     13,
                      color:        'var(--text)',
                      lineHeight:   '1.6',
                      whiteSpace:   'pre-wrap',
                    }}>
                      {m.message}
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>
            {msgErr && (
              <div style={{ padding:'0 26px 10px', color:'var(--danger)', fontSize:12 }}>⚠ {msgErr}</div>
            )}
            <div style={{ borderTop:'1px solid var(--border)', padding:'14px 26px', display:'flex', gap:10 }}>
              <input
                className="finput"
                style={{ flex:1 }}
                placeholder="Type a message… (Enter to send)"
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                disabled={sending}
              />
              <button className="btn btn-primary" onClick={sendMessage} disabled={sending || !msgText.trim()}>
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NEW ORDER MODAL ──────────────────────────────────────────────────────────
function NewOrderModal({ onClose, onSubmit, companyId, prefill }) {
  const MAX_HEIGHT_INCHES = 52;

  // pricePerFt is stored in both a ref (for use inside async callbacks) and state (for rendering)
  const pricePerFtRef = useRef(9.50);
  const [pricePerFt,   setPricePerFtState] = useState(9.50);
  const setPricePerFt  = (v) => { pricePerFtRef.current = v; setPricePerFtState(v); };

  const [priceMap,     setPriceMap]     = useState({}); // { materialName: price_per_linear_foot }
  const [materialOpts, setMaterialOpts] = useState(MATERIALS);

  const [form,      setForm]      = useState({
    vehicle:  prefill?.vehicle  || '',
    size:     prefill?.size     || 'Full Wrap',
    material: prefill?.material || MATERIALS[0],
    laminate: prefill?.laminate || 'Gloss',
    quantity: prefill?.quantity || 1,
    notes:    '',
  });
  const [files,     setFiles]     = useState([]);
  const [refFiles,  setRefFiles]  = useState([]);
  const [dragOver,  setDragOver]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [error,     setError]     = useState('');
  const [checklist, setChecklist] = useState({c1:false,c2:false,c3:false,c4:false,c5:false,c6:false});
  const [discountPct, setDiscountPct] = useState(0);
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [saveVehicle, setSaveVehicle] = useState(false);
  const allChecked = Object.values(checklist).every(Boolean);
  const fileRef    = useRef();
  const refFileRef = useRef();
  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));

  // Load materials from products table + company pricing overrides + discount + saved vehicles
  useEffect(() => {
    const load = async () => {
      const { data: prods } = await supabase
        .from('products').select('id, name, base_price').eq('is_active', true).order('name', { ascending: true });
      if (!prods || prods.length === 0) return;

      let overrides = [];
      if (companyId) {
        const { data: ovs } = await supabase
          .from('company_product_overrides').select('product_id, price_per_linear_foot').eq('company_id', companyId);
        overrides = ovs || [];

        // Fetch company discount
        const { data: co } = await supabase.from('companies').select('discount_pct').eq('id', companyId).single();
        setDiscountPct(Number(co?.discount_pct) || 0);

        // Fetch saved vehicles
        const { data: sv } = await supabase.from('saved_vehicles').select('*').eq('company_id', companyId).order('name');
        setSavedVehicles(sv || []);
      }

      // Build map: materialName → effective price per linear foot
      const map = {};
      prods.forEach(p => {
        const ov = overrides.find(o => o.product_id === p.id);
        map[p.name] = ov ? Number(ov.price_per_linear_foot) : Number(p.base_price) || 9.50;
      });

      const names       = prods.map(p => p.name);
      const initialMat  = prefill?.material && map[prefill.material] ? prefill.material : names[0];
      const initialPrice = map[initialMat] || 9.50;

      setPriceMap(map);
      setMaterialOpts(names);
      setForm(f => ({ ...f, material: initialMat }));
      setPricePerFt(initialPrice);
    };
    load();
  }, []); // eslint-disable-line

  // When material changes, update pricePerFt and recalculate existing file prices
  const handleMaterialChange = (mat) => {
    set('material', mat);
    const newPrice = priceMap[mat] || pricePerFtRef.current;
    setPricePerFt(newPrice);
    setFiles(prev => prev.map(f =>
      f.linearFt ? { ...f, price: f.linearFt * newPrice } : f
    ));
  };

  const readPdfDimensions = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        if (!window.pdfjsLib) {
          await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }
        const typedArray = new Uint8Array(e.target.result);
        const pdf        = await window.pdfjsLib.getDocument({ data: typedArray }).promise;
        const page       = await pdf.getPage(1);
        const viewport   = page.getViewport({ scale:1 });
        const widthIn    = (viewport.width  / 72) * 10;
        const heightIn   = (viewport.height / 72) * 10;
        const lengthIn   = Math.max(widthIn, heightIn);
        const shortIn    = Math.min(widthIn, heightIn);
        const linearFt   = lengthIn / 12;
        const price      = linearFt * pricePerFtRef.current;
        const warning    = shortIn > MAX_HEIGHT_INCHES
          ? `⚠ Short dimension is ${shortIn.toFixed(1)}" — exceeds ${MAX_HEIGHT_INCHES}" max media width.` : null;
        resolve({ widthIn, heightIn, lengthIn, shortIn, linearFt, price, warning, error:null });
      } catch {
        resolve({ error:'Could not read PDF dimensions. Make sure it is a valid, uncorrupted PDF.' });
      }
    };
    reader.onerror = () => resolve({ error:'Could not read file.' });
    reader.readAsArrayBuffer(file);
  });

  const addFiles = async (fl) => {
    const isPdf = f => f.name.toLowerCase().endsWith('.pdf');
    const isEps = f => f.name.toLowerCase().endsWith('.eps');
    const pdfs    = Array.from(fl).filter(isPdf);
    const epss    = Array.from(fl).filter(isEps);
    const skipped = Array.from(fl).filter(f => !isPdf(f) && !isEps(f));
    if (skipped.length > 0) setError(`Only PDF and EPS files accepted. Skipped: ${skipped.map(f=>f.name).join(', ')}`);
    const processedPdfs = await Promise.all(pdfs.map(async (file) => {
      const dims = await readPdfDimensions(file);
      return { file, name: file.name, size: file.size, ...dims };
    }));
    const processedEps = epss.map(file => ({ file, name: file.name, size: file.size, eps: true, error: null }));
    setFiles(p => [...p, ...processedPdfs, ...processedEps]);
  };

  const removeFile = (i) => setFiles(p => p.filter((_,fi) => fi !== i));

  const updateEpsDims = (i, widthIn, heightIn) => {
    const w = parseFloat(widthIn) || 0;
    const h = parseFloat(heightIn) || 0;
    const lengthIn = Math.max(w, h);
    const shortIn  = Math.min(w, h);
    const linearFt = lengthIn / 12;
    const price    = linearFt * pricePerFtRef.current;
    const warning  = shortIn > 0 && shortIn > MAX_HEIGHT_INCHES
      ? `⚠ Short dimension is ${shortIn.toFixed(1)}" — exceeds ${MAX_HEIGHT_INCHES}" max media width.` : null;
    setFiles(p => p.map((f, fi) => fi === i ? { ...f, widthIn: w, heightIn: h, linearFt, price, warning } : f));
  };

  const addRefFiles = (fl) => {
    const allowed = ['.jpg','.jpeg','.png','.ai','.svg'];
    const valid   = Array.from(fl).filter(f => allowed.some(ext => f.name.toLowerCase().endsWith(ext)));
    const skipped = Array.from(fl).filter(f => !allowed.some(ext => f.name.toLowerCase().endsWith(ext)));
    if (skipped.length > 0) setError(`Reference files: only JPG, PNG, AI, SVG accepted. Skipped: ${skipped.map(f=>f.name).join(', ')}`);
    setRefFiles(p => [...p, ...valid.map(f => ({ file:f, name:f.name, size:f.size }))]);
  };

  const validFiles    = files.filter(f => !f.error);
  const totalLinearFt = validFiles.reduce((s,f) => s + (f.linearFt||0), 0) * form.quantity;
  const totalPrice    = validFiles.reduce((s,f) => s + (f.price||0), 0) * form.quantity;
  const hasErrors     = files.some(f => f.error);

  const submit = async () => {
    if (!form.vehicle.trim()) { setError('Vehicle name is required.'); return; }
    if (files.length === 0)   { setError('Please upload at least one PDF or EPS file.'); return; }
    if (hasErrors)            { setError('Fix file errors before submitting.'); return; }
    setUploading(true); setError(''); setProgress(10);

    try {
      const orderId = genOrderId();
      const pricingSummary = validFiles.map(f =>
        f.linearFt
          ? `${f.name}: ${f.linearFt.toFixed(2)} lin ft @ $${pricePerFtRef.current.toFixed(2)}/ft = $${f.price.toFixed(2)}`
          : `${f.name}: EPS — dimensions not provided, priced after review`
      ).join('\n');

      const discountAmt = totalPrice * (discountPct / 100);
      const finalPrice  = totalPrice - discountAmt;

      const { error: oErr } = await supabase.from('orders').insert({
        id:          orderId,
        company_id:  companyId,
        vehicle:     form.vehicle.trim(),
        size:        form.size,
        material:    form.material,
        laminate:    form.laminate,
        quantity:    form.quantity,
        notes:       form.notes,
        status:      'Pending Review',
        admin_notes: `AUTO-PRICING SUMMARY:\n${pricingSummary}\n\nTotal linear ft: ${totalLinearFt.toFixed(2)}\nSubtotal (qty ${form.quantity}): $${totalPrice.toFixed(2)}${discountPct > 0 ? `\nPartner discount (${discountPct}%): -$${discountAmt.toFixed(2)}\nFinal price: $${finalPrice.toFixed(2)}` : ''}`,
      });
      if (oErr) throw oErr;

      // Save vehicle for quick reorder if requested
      if (saveVehicle && form.vehicle.trim()) {
        await supabase.from('saved_vehicles').upsert(
          { company_id: companyId, name: form.vehicle.trim() },
          { onConflict: 'company_id,name' }
        );
      }

      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          order_id:    orderId,
          vehicle:     form.vehicle.trim(),
          size:        form.size,
          material:    form.material,
          laminate:    form.laminate,
          quantity:    form.quantity,
          total_price: `$${totalPrice.toFixed(2)}`,
          linear_ft:   totalLinearFt.toFixed(2),
          notes:       form.notes || '(none)',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      ).catch(() => {});

      setProgress(30);

      const fileRecords = [];
      const allFiles = files.length + refFiles.length;
      for (let i = 0; i < files.length; i++) {
        const f    = files[i];
        const path = `${companyId}/${orderId}/${Date.now()}-${f.name}`;
        const { error: fErr } = await supabase.storage.from('wrap-files').upload(path, f.file);
        if (fErr) throw new Error(`File "${f.name}": ${fErr.message}`);
        fileRecords.push({ order_id:orderId, file_name:f.name, file_path:path, file_size:f.size });
        setProgress(30 + Math.round(60 * (i+1) / allFiles));
      }
      for (let i = 0; i < refFiles.length; i++) {
        const f    = refFiles[i];
        const path = `${companyId}/${orderId}/ref-${Date.now()}-${f.name}`;
        const { error: rfErr } = await supabase.storage.from('wrap-files').upload(path, f.file);
        if (rfErr) throw new Error(`Reference file "${f.name}": ${rfErr.message}`);
        fileRecords.push({ order_id:orderId, file_name:f.name, file_path:path, file_size:f.size });
        setProgress(30 + Math.round(60 * (files.length + i + 1) / allFiles));
      }

      if (fileRecords.length > 0) {
        const { error: frErr } = await supabase.from('order_files').insert(fileRecords);
        if (frErr) throw frErr;
      }

      setProgress(100);
      try { await onSubmit({ id: orderId, vehicle: form.vehicle.trim(), company_id: companyId }); } catch {}
      onClose();
    } catch (e) {
      setError(e.message || 'Upload failed. Please try again.');
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="overlay" onClick={uploading ? undefined : onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:'22px 26px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(0,0,0,.2)' }}>
          <div>
            <div className="bc" style={{ fontSize:22, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff' }}>New Wrap Order</div>
            <div style={{ color:'var(--text-2)', fontSize:12, marginTop:3 }}>${pricePerFt.toFixed(2)} / linear foot · PDF auto-pricing</div>
          </div>
          {!uploading && <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:22, cursor:'pointer', padding:'2px 6px', borderRadius:4 }}>×</button>}
        </div>

        {uploading && <div style={{ height:3, background:'var(--border)' }}><div className="progress-bar" style={{ width:`${progress}%` }} /></div>}

        <div style={{ padding:26 }}>
          {/* Saved vehicle quick select */}
          {savedVehicles.length > 0 && (
            <div style={{ marginBottom:8 }}>
              <select className="fselect" value="" onChange={e => { if (e.target.value) set('vehicle', e.target.value); }}>
                <option value="">— Select saved vehicle —</option>
                {savedVehicles.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
              </select>
            </div>
          )}
          <FInput label="Vehicle / Job Name *" placeholder="e.g. NASCAR Cup Car #14" value={form.vehicle} onChange={e => set('vehicle', e.target.value)} disabled={uploading} />
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--text-2)', marginBottom:16, marginTop:-8 }}>
            <input type="checkbox" checked={saveVehicle} onChange={e => setSaveVehicle(e.target.checked)} />
            Save this vehicle for quick reorder
          </label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <FSelect label="Wrap Coverage" opts={SIZES} value={form.size} onChange={e => set('size', e.target.value)} disabled={uploading} />
            <div style={{ marginBottom:16 }}>
              <label className="flabel">Quantity (Sets)</label>
              <input className="finput" type="number" min="1" max="50" value={form.quantity} onChange={e => set('quantity', parseInt(e.target.value)||1)} disabled={uploading} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <FSelect label="Print Material" opts={materialOpts} value={form.material} onChange={e => handleMaterialChange(e.target.value)} disabled={uploading} />
            <FSelect label="Laminate" opts={LAMINATES} value={form.laminate} onChange={e => set('laminate', e.target.value)} disabled={uploading} />
          </div>

          {/* Artwork checklist */}
          <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'16px 20px', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div className="section-label" style={{ margin:0 }}>File Requirements Checklist</div>
              {allChecked && (
                <span style={{ color:'var(--success)', fontSize:12, fontWeight:700, letterSpacing:'1px' }}>✓ Ready to upload</span>
              )}
            </div>
            {[
              ['c1','Files are in CMYK color mode (not RGB)'],
              ['c2','Artwork is at 100% scale (or 1:10 scale for large format)'],
              ['c3','Minimum 1.5" bleed on all edges'],
              ['c4','Maximum height does not exceed 52"'],
              ['c5','Resolution is 100+ DPI at full size'],
              ['c6','Fonts are outlined / embedded'],
            ].map(([key, label], idx, arr) => (
              <div
                key={key}
                onClick={() => setChecklist(p => ({ ...p, [key]: !p[key] }))}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', cursor:'pointer', borderBottom: idx < arr.length-1 ? '1px solid var(--border-sub)' : 'none' }}
              >
                <div style={{
                  width:18, height:18, borderRadius:4, flexShrink:0,
                  border: checklist[key] ? 'none' : '1px solid var(--border)',
                  background: checklist[key] ? 'var(--accent)' : 'var(--bg-input)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:12, color:'#000', fontWeight:900,
                }}>
                  {checklist[key] ? '✓' : ''}
                </div>
                <span style={{ fontSize:13, color: checklist[key] ? 'var(--text)' : 'var(--text-2)', userSelect:'none' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* File upload */}
          <div style={{ marginBottom:16 }}>
            <label className="flabel">Upload Print Files (PDF or EPS · 100% scale)</label>
            <div
              className={`drop-zone ${dragOver ? 'over' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              onClick={() => !uploading && fileRef.current.click()}
            >
              <div style={{ fontSize:30, marginBottom:8, opacity:.6 }}>📁</div>
              <div className="bc" style={{ color:'var(--text-2)', fontSize:15, fontWeight:700, letterSpacing:'1px' }}>
                {uploading ? `Uploading… ${progress}%` : 'Drop PDF or EPS files here, or click to browse'}
              </div>
              <div style={{ color:'var(--text-3)', fontSize:11, marginTop:5 }}>
                CMYK · 100% scale · 1.5" bleed · max 52" tall · ${pricePerFt.toFixed(2)}/linear ft · PDF auto-priced, EPS priced after review
              </div>
              <input ref={fileRef} type="file" accept=".pdf,.eps" multiple style={{ display:'none' }} onChange={e => addFiles(e.target.files)} />
            </div>

            {files.map((f, i) => (
              <div key={i} style={{ marginTop:10, background:'var(--bg-elevated)', border:`1px solid ${f.error ? 'var(--danger)' : f.warning ? 'var(--warn)' : f.eps ? 'rgba(139,92,246,0.35)' : 'var(--border)'}`, borderRadius:'var(--r-sm)', padding:'14px 16px' }}>
                {f.eps ? (
                  <div>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                          <span style={{ color:'#8b5cf6', fontSize:16 }}>📐</span>
                          <span style={{ color:'var(--text)', fontSize:13, fontWeight:600 }}>{f.name}</span>
                          <span style={{ color:'var(--text-2)', fontSize:11 }}>({(f.size/1024/1024).toFixed(1)} MB)</span>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:8 }}>
                          <div>
                            <div style={{ color:'var(--text-2)', fontSize:9, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', marginBottom:5 }}>Width (inches)</div>
                            <input
                              className="finput"
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder='e.g. 240"'
                              value={f.widthIn || ''}
                              onChange={e => updateEpsDims(i, e.target.value, f.heightIn)}
                              disabled={uploading}
                              style={{ padding:'8px 10px', fontSize:14 }}
                            />
                          </div>
                          <div>
                            <div style={{ color:'var(--text-2)', fontSize:9, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', marginBottom:5 }}>Height (inches)</div>
                            <input
                              className="finput"
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder='e.g. 48"'
                              value={f.heightIn || ''}
                              onChange={e => updateEpsDims(i, f.widthIn, e.target.value)}
                              disabled={uploading}
                              style={{ padding:'8px 10px', fontSize:14 }}
                            />
                          </div>
                        </div>
                        {f.linearFt > 0 && (
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                            {[
                              { l:'Linear Ft',  v:`${f.linearFt.toFixed(2)} ft` },
                              { l:'Unit Price', v:`$${f.price.toFixed(2)}` },
                            ].map(d => (
                              <div key={d.l} style={{ background:'var(--bg-input)', padding:'8px 10px', borderRadius:'var(--r-sm)', border:'1px solid var(--border-sub)' }}>
                                <div style={{ color:'var(--text-2)', fontSize:9, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', marginBottom:4 }}>{d.l}</div>
                                <div className="bc" style={{ color: d.l === 'Unit Price' ? 'var(--success)' : 'var(--text)', fontSize:16, fontWeight:700 }}>{d.v}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {!f.linearFt && <div style={{ color:'#8b5cf6', fontSize:11 }}>Enter dimensions above to calculate price</div>}
                        {f.warning && <div style={{ color:'var(--warn)', fontSize:11, marginTop:6 }}>{f.warning}</div>}
                      </div>
                      {!uploading && <button onClick={() => removeFile(i)} style={{ background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', fontSize:18, flexShrink:0 }}>×</button>}
                    </div>
                  </div>
                ) : f.error ? (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ color:'var(--danger)' }}>✕</span>
                        <span style={{ color:'var(--text)', fontSize:13 }}>{f.name}</span>
                      </div>
                      <div style={{ color:'var(--danger)', fontSize:11, marginTop:4 }}>{f.error}</div>
                    </div>
                    <button onClick={() => removeFile(i)} style={{ background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', fontSize:16 }}>×</button>
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                        <span style={{ color:'var(--accent)', fontSize:16 }}>📄</span>
                        <span style={{ color:'var(--text)', fontSize:13, fontWeight:600 }}>{f.name}</span>
                        <span style={{ color:'var(--text-2)', fontSize:11 }}>({(f.size/1024/1024).toFixed(1)} MB)</span>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                        {[
                          { l:'Width',      v:`${f.widthIn.toFixed(2)}"` },
                          { l:'Height',     v:`${f.heightIn.toFixed(2)}"` },
                          { l:'Linear Ft',  v:`${f.linearFt.toFixed(2)} ft` },
                          { l:'Unit Price', v:`$${f.price.toFixed(2)}` },
                        ].map(d => (
                          <div key={d.l} style={{ background:'var(--bg-input)', padding:'8px 10px', borderRadius:'var(--r-sm)', border:'1px solid var(--border-sub)' }}>
                            <div style={{ color:'var(--text-2)', fontSize:9, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', marginBottom:4 }}>{d.l}</div>
                            <div className="bc" style={{ color: d.l === 'Unit Price' ? 'var(--success)' : 'var(--text)', fontSize:16, fontWeight:700 }}>{d.v}</div>
                          </div>
                        ))}
                      </div>
                      {f.warning && <div style={{ color:'var(--warn)', fontSize:11, marginTop:8 }}>{f.warning}</div>}
                    </div>
                    {!uploading && <button onClick={() => removeFile(i)} style={{ background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', fontSize:18, flexShrink:0 }}>×</button>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Reference / brand asset upload */}
          <div style={{ marginBottom:16 }}>
            <label className="flabel">Reference Images & Brand Assets (optional)</label>
            <div
              style={{ border:'1px dashed var(--border)', background:'var(--bg-input)', borderRadius:'var(--r)', padding:'20px', textAlign:'center', cursor:'pointer' }}
              onClick={() => !uploading && refFileRef.current.click()}
            >
              <div style={{ color:'var(--text-2)', fontSize:13 }}>
                🖼 Drop JPG, PNG, AI, or SVG files here, or click to browse
              </div>
              <div style={{ color:'var(--text-3)', fontSize:11, marginTop:4 }}>Logos, livery references, sponsor artwork</div>
              <input ref={refFileRef} type="file" accept=".jpg,.jpeg,.png,.ai,.svg" multiple style={{ display:'none' }} onChange={e => addRefFiles(e.target.files)} />
            </div>
            {refFiles.map((f,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--bg-elevated)', border:'1px solid rgba(139,92,246,0.35)', borderRadius:'var(--r-sm)', padding:'10px 14px', marginTop:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ color:'#8b5cf6' }}>🖼</span>
                  <span style={{ color:'var(--text)', fontSize:13 }}>{f.name}</span>
                  <span style={{ color:'var(--text-2)', fontSize:11 }}>({(f.size/1024/1024).toFixed(1)} MB)</span>
                </div>
                {!uploading && <button onClick={() => setRefFiles(p => p.filter((_,fi) => fi !== i))} style={{ background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', fontSize:16 }}>×</button>}
              </div>
            ))}
          </div>

          {/* Price summary */}
          {validFiles.length > 0 && (
            <div style={{ background:'rgba(16,185,129,0.06)', border:'1px solid rgba(16,185,129,0.18)', borderRadius:'var(--r)', padding:'18px 20px', marginBottom:16 }}>
              <div className="section-label" style={{ color:'var(--success)', marginBottom:12 }}>Order Estimate</div>
              {(() => {
                const discountAmt = totalPrice * (discountPct / 100);
                const finalPrice  = totalPrice - discountAmt;
                return (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                    <div style={{ fontSize:13, color:'var(--text-2)', lineHeight:1.8 }}>
                      {validFiles.length} file{validFiles.length !== 1 ? 's' : ''}<br />
                      {(totalLinearFt / form.quantity).toFixed(2)} lin ft × {form.quantity} set{form.quantity !== 1 ? 's' : ''}<br />
                      @ ${pricePerFt.toFixed(2)}/ft
                      {discountPct > 0 && (
                        <>
                          <br />
                          <span style={{ color:'var(--success)' }}>- ${discountAmt.toFixed(2)} ({discountPct}% partner discount)</span>
                        </>
                      )}
                    </div>
                    <div style={{ textAlign:'right' }}>
                      {discountPct > 0 && (
                        <div style={{ color:'var(--text-3)', fontSize:13, textDecoration:'line-through', marginBottom:2 }}>${totalPrice.toFixed(2)}</div>
                      )}
                      <div className="bc" style={{ fontSize:40, fontWeight:900, color:'var(--success)', lineHeight:1 }}>
                        ${discountPct > 0 ? finalPrice.toFixed(2) : totalPrice.toFixed(2)}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div style={{ color:'rgba(16,185,129,0.35)', fontSize:11, marginTop:10 }}>
                * Estimate only. Final invoice issued after file review.
              </div>
            </div>
          )}

          <div style={{ marginBottom:20 }}>
            <label className="flabel">Notes / Special Instructions</label>
            <textarea className="finput" rows={3} style={{ resize:'vertical' }} placeholder="Rush deadlines, sponsor info, special instructions…" value={form.notes} onChange={e => set('notes', e.target.value)} disabled={uploading} />
          </div>

          {error && (
            <div style={{ color:'var(--danger)', fontSize:13, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
              <span>⚠</span> {error}
            </div>
          )}

          <div style={{ display:'flex', gap:10 }}>
            {!uploading && <button className="btn btn-ghost" onClick={onClose}>Cancel</button>}
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={!form.vehicle.trim() || files.length === 0 || hasErrors || uploading || !allChecked}
              style={{ flex:1 }}
            >
              {uploading
                ? `Uploading ${progress}%…`
                : totalPrice > 0
                  ? `Submit Order — Est. $${(totalPrice * (1 - discountPct/100)).toFixed(2)}`
                  : 'Submit Order →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── COMPANY MODAL (admin) ────────────────────────────────────────────────────
function CompanyModal({ company, onClose, onSave, onDelete, onPasswordReset, existingCount }) {
  const isEdit = !!company;
  const [form, setForm] = useState(company
    ? { ...company, newPassword:'', discount_pct: company.discount_pct || 0 }
    : { company:'', email:'', newPassword:'', contact:'', phone:'', account_num:`UPC-${String(existingCount+1).padStart(3,'0')}`, active:true, notes:'', discount_pct:0 });
  const [saving,     setSaving]     = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [error,      setError]      = useState('');
  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const handleSave = async () => {
    if (!form.company || !form.email) { setError('Company name and email are required.'); return; }
    if (!isEdit && !form.newPassword) { setError('Password is required for new partners.'); return; }
    setSaving(true); setError('');
    try { await onSave(form); } catch(e) { setError(e.message); setSaving(false); }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ padding:'22px 26px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(0,0,0,.2)' }}>
          <div className="bc" style={{ fontSize:20, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff' }}>
            {isEdit ? 'Edit Partner' : 'New Partner'}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:22, cursor:'pointer', padding:'2px 6px', borderRadius:4 }}>×</button>
        </div>
        <div style={{ padding:26 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div style={{ gridColumn:'1/-1' }}><FInput label="Company Name *" value={form.company} onChange={e => set('company', e.target.value)} /></div>
            <FInput label="Account Number" value={form.account_num||''} onChange={e => set('account_num', e.target.value)} />
            <FInput label="Contact Name" value={form.contact||''} onChange={e => set('contact', e.target.value)} />
            <FInput label="Login Email *" type="email" value={form.email} onChange={e => set('email', e.target.value.toLowerCase())} disabled={isEdit} />
            <FInput label="Phone" value={form.phone||''} onChange={e => set('phone', e.target.value)} />
            <FInput label="Partner Discount (%)" type="number" min="0" max="100" step="0.5" value={form.discount_pct||0} onChange={e => set('discount_pct', e.target.value)} />
            <div style={{ gridColumn:'1/-1' }}>
              {isEdit ? (
                <div style={{ marginBottom:16 }}>
                  <label className="flabel">Password</label>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width:'100%', justifyContent:'center' }}
                    onClick={() => onPasswordReset && onPasswordReset(form.email)}
                  >
                    Send Password Reset Email →
                  </button>
                </div>
              ) : (
                <FInput
                  label="Password *"
                  type="text"
                  placeholder="Set a secure password"
                  value={form.newPassword||''}
                  onChange={e => set('newPassword', e.target.value)}
                />
              )}
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <label className="flabel">Internal Notes</label>
              <textarea className="finput" rows={2} style={{ resize:'vertical', marginBottom:16 }} placeholder="Payment terms, preferences…" value={form.notes||''} onChange={e => set('notes', e.target.value)} />
            </div>
            {isEdit && (
              <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:10 }}>
                <span className="flabel" style={{ margin:0 }}>Account Status</span>
                <button
                  onClick={() => set('active', !form.active)}
                  style={{
                    background: form.active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${form.active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    color: form.active ? 'var(--success)' : 'var(--danger)',
                    padding:'6px 16px', fontFamily:"'Space Grotesk',sans-serif",
                    fontSize:12, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase',
                    cursor:'pointer', borderRadius:'var(--r-sm)',
                  }}
                >
                  {form.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                </button>
              </div>
            )}
          </div>
          {error && <div style={{ color:'var(--danger)', fontSize:13, margin:'12px 0', display:'flex', gap:6 }}><span>⚠</span>{error}</div>}
          <div className="divider" />
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {isEdit && (!confirmDel
              ? <button className="btn btn-danger" onClick={() => setConfirmDel(true)}>Delete</button>
              : <button className="btn btn-danger-confirm" onClick={() => onDelete(form.id)}>Confirm Delete?</button>
            )}
            <div style={{ flex:1 }} />
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Company'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PAYMENT MODAL ────────────────────────────────────────────────────────────
function PaymentModal({ order, company, onClose, onSubmitted }) {
  const [mode,    setMode]    = useState('card'); // 'card' | 'manual'
  const [amount,  setAmount]  = useState('');
  const [form,    setForm]    = useState({ payment_method:'check', reference_number:'', notes:'' });
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [err,     setErr]     = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const base     = parseFloat(amount) || 0;
  const shipping = 50;
  const subtotal = base + shipping;
  const ccFee    = Math.round(subtotal * 0.035 * 100) / 100;
  const total    = subtotal + ccFee;

  const handleStripe = async () => {
    if (!base || base <= 0) { setErr('Enter a valid order amount.'); return; }
    setSaving(true); setErr('');
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          order_id:          order.id,
          company_id:        company.id,
          order_vehicle:     order.vehicle,
          base_amount_cents: Math.round(base * 100),
        },
      });
      if (error || !data?.url) throw new Error(error?.message || 'Could not create checkout session.');
      window.location.href = data.url;
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  const handleManual = async () => {
    if (!base || base <= 0) { setErr('Please enter a valid amount.'); return; }
    setSaving(true); setErr('');
    try {
      const { error } = await supabase.from('payments').insert({
        order_id:         order.id,
        company_id:       company.id,
        amount:           base,
        payment_method:   form.payment_method,
        reference_number: form.reference_number || null,
        notes:            form.notes || null,
        status:           'pending',
      });
      if (error) throw error;
      setSuccess(true);
      try { await onSubmitted?.(); } catch {}
    } catch (e) {
      setErr(e.message || 'Submission failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ label, value, accent }) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border-sub)', fontSize:13 }}>
      <span style={{ color:'var(--text-2)' }}>{label}</span>
      <span style={{ color: accent ? 'var(--text)' : 'var(--text)', fontWeight: accent ? 600 : 400 }}>{value}</span>
    </div>
  );

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div className="bc" style={{ fontSize:18, fontWeight:700, color:'var(--text)' }}>Submit Payment</div>
            <div style={{ color:'var(--text-2)', fontSize:12, marginTop:3 }}>Order {order.id} · {order.vehicle}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:20, cursor:'pointer', padding:'2px 6px', borderRadius:4 }}>×</button>
        </div>

        {success ? (
          <div style={{ padding:40, textAlign:'center' }}>
            <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, margin:'0 auto 16px' }}>✓</div>
            <div className="bc" style={{ fontSize:18, fontWeight:700, color:'var(--success)', marginBottom:8 }}>Payment Submitted</div>
            <div style={{ color:'var(--text-2)', fontSize:13, marginBottom:28 }}>Pending confirmation from our team.</div>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <div style={{ padding:24 }}>
            {/* Mode toggle */}
            <div style={{ display:'flex', background:'var(--bg-elevated)', borderRadius:6, padding:3, marginBottom:22, border:'1px solid var(--border)' }}>
              {[['card','Pay with Card'],['manual','Check / Wire']].map(([v,l]) => (
                <button key={v} onClick={() => { setMode(v); setErr(''); }}
                  style={{ flex:1, padding:'7px 0', fontSize:12, fontWeight:600, fontFamily:"'Inter',sans-serif",
                    borderRadius:4, border:'none', cursor:'pointer', transition:'all .15s',
                    background: mode === v ? 'var(--accent)' : 'transparent',
                    color:      mode === v ? '#000' : 'var(--text-2)',
                  }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Amount (shared) */}
            <FInput
              label="Order Amount ($)"
              placeholder="e.g. 850.00"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={e => { setAmount(e.target.value); setErr(''); }}
            />

            {mode === 'card' ? (
              <>
                {/* Fee breakdown */}
                {base > 0 && (
                  <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:'4px 14px', marginBottom:20 }}>
                    <Row label="Order Total"           value={`$${base.toFixed(2)}`} />
                    <Row label="Shipping & Handling"    value="$50.00" />
                    <Row label="CC Processing Fee (3.5%)" value={`$${ccFee.toFixed(2)}`} />
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', fontSize:14 }}>
                      <span style={{ fontWeight:700, color:'var(--text)' }}>Charged Today</span>
                      <span style={{ fontWeight:700, color:'var(--accent)', fontSize:16 }}>${total.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                {err && <div style={{ color:'var(--danger)', fontSize:12, marginBottom:14, padding:'9px 12px', background:'rgba(239,68,68,0.08)', borderRadius:'var(--r-sm)' }}>{err}</div>}
                <button className="btn btn-primary" style={{ width:'100%', padding:13, fontSize:14, justifyContent:'center' }}
                  onClick={handleStripe} disabled={saving || !base}>
                  {saving ? 'Redirecting to Stripe…' : base > 0 ? `Pay $${total.toFixed(2)} with Card →` : 'Pay with Card →'}
                </button>
                <div style={{ textAlign:'center', marginTop:10, color:'var(--text-3)', fontSize:11 }}>
                  Secured by Stripe · Your card info never touches our servers
                </div>
              </>
            ) : (
              <>
                <FSelect label="Payment Method" opts={['check','wire']} value={form.payment_method} onChange={e => set('payment_method', e.target.value)} />
                <FInput label="Reference / Check Number (optional)" placeholder="e.g. CHK-1042" value={form.reference_number} onChange={e => set('reference_number', e.target.value)} />
                <div style={{ marginBottom:16 }}>
                  <label className="flabel">Notes (optional)</label>
                  <textarea className="finput" rows={3} style={{ resize:'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional payment details…" />
                </div>
                {err && <div style={{ color:'var(--danger)', fontSize:12, marginBottom:12, padding:'9px 12px', background:'rgba(239,68,68,0.08)', borderRadius:'var(--r-sm)' }}>{err}</div>}
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleManual} disabled={saving}>
                    {saving ? 'Submitting…' : 'Submit Payment'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CLIENT PORTAL ────────────────────────────────────────────────────────────
function ClientPortal({ session, company, onLogout, theme, onThemeToggle }) {
  const [view,     setView]     = useState('dashboard');
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selOrder,     setSelOrder]     = useState(null);
  const [showNew,      setShowNew]      = useState(false);
  const [payOrder,     setPayOrder]     = useState(null);
  const [reorderOrder, setReorderOrder] = useState(null);
  const [notif,    setNotif]    = useState(null);
  const [savedVehiclesList, setSavedVehiclesList] = useState([]);
  const [newVehicleName,    setNewVehicleName]    = useState('');
  const [sidebarOpen,       setSidebarOpen]       = useState(false);
  const notify = (msg, type='success') => { setNotif({ msg, type }); setTimeout(() => setNotif(null), 3500); };

  const loadOrders = useCallback(async () => {
    const { data, error } = await supabase
      .from('orders')
      .select('*, files:order_files(*), payments:payments(*)')
      .eq('company_id', company.id)
      .order('created_at', { ascending:false });
    if (!error) setOrders(data || []);
    setLoading(false);
  }, [company.id]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Handle Stripe return URLs
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_success')) {
      loadOrders();
      notify('Payment successful! Your order is confirmed.', 'success');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('stripe_cancelled')) {
      notify('Payment cancelled — no charge was made.', 'warn');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line

  // Load saved vehicles for Account tab
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('saved_vehicles').select('*').eq('company_id', company.id).order('name');
      setSavedVehiclesList(data || []);
    };
    load();
  }, [company.id]);

  const updateOrder = async (updated) => {
    const { error } = await supabase.from('orders').update({
      status:          updated.status,
      tracking_number: updated.tracking_number,
    }).eq('id', updated.id);
    if (error) { notify('Save failed.', 'error'); return; }
    await loadOrders();
    notify('Order updated.');
  };

  const active = orders.filter(o => o.status !== 'Completed');

  const wrap = (content) => (
    <div className="main-layout" style={{ background:'var(--bg)', color:'var(--text)' }}>
      <style>{CSS}</style>
      <Sidebar
        items={[
          ['dashboard', 'Dashboard', Icons.dashboard],
          ['orders',    'Orders',    Icons.orders],
          ['account',   'Account',   Icons.account],
        ]}
        active={view} onNav={setView} onLogout={onLogout}
        theme={theme} onThemeToggle={onThemeToggle}
        userEmail={session.user.email}
        open={sidebarOpen} onClose={() => setSidebarOpen(false)}
      />
      <div className="main-content">
        <div className="mobile-bar">
          <button className="hamburger" onClick={() => setSidebarOpen(true)}><span/><span/><span/></button>
          <Logo height={22} />
          <div style={{ width:36 }} />
        </div>
        <Notif n={notif} />
        <div className="main-inner">
          {loading ? <div style={{ color:'var(--text-3)', textAlign:'center', paddingTop:100, fontSize:13 }}>Loading…</div> : content}
        </div>
      </div>
      {selOrder     && <OrderModal order={selOrder} company={company} onClose={() => setSelOrder(null)} onUpdate={async u => { await updateOrder(u); setSelOrder(null); }} isAdmin={false} senderEmail={session.user.email} senderRole="partner" />}
      {showNew      && <NewOrderModal companyId={company.id} onClose={() => setShowNew(false)} onSubmit={async (newOrder) => { await loadOrders(); if (newOrder) setPayOrder(newOrder); }} />}
      {reorderOrder && <NewOrderModal companyId={company.id} prefill={reorderOrder} onClose={() => setReorderOrder(null)} onSubmit={async (newOrder) => { await loadOrders(); if (newOrder) setPayOrder(newOrder); }} />}
      {payOrder     && <PaymentModal order={payOrder} company={company} onClose={() => setPayOrder(null)} onSubmitted={async () => { await loadOrders(); notify('Payment submitted!'); }} />}
    </div>
  );

  const quoteSentOrders = orders.filter(o => o.status === 'Quote Sent');
  const QuoteBanner = () => quoteSentOrders.length === 0 ? null : (
    <div style={{ marginBottom:20 }}>
      {quoteSentOrders.map(o => (
        <div key={o.id} style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'var(--r)', padding:'12px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:18 }}>⚡</span>
            <span style={{ color:'#f59e0b', fontWeight:700, fontSize:13 }}>
              Quote Ready for Approval: <span style={{ color:'var(--text)' }}>{o.vehicle}</span> — view order to approve
            </span>
          </div>
          <button className="btn btn-sm btn-ghost" style={{ borderColor:'rgba(245,158,11,0.4)', color:'#f59e0b' }} onClick={() => setSelOrder(o)}>View →</button>
        </div>
      ))}
    </div>
  );

  if (view === 'dashboard') return wrap(
    <div>
      {/* Page header */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:32, flexWrap:'wrap', gap:14 }}>
        <div>
          <div style={{ color:'var(--text-2)', fontSize:11, fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', marginBottom:6 }}>Welcome back</div>
          <div className="bc" style={{ fontSize:38, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', lineHeight:1, color:'#fff' }}>
            {company.company}
          </div>
          <div style={{ color:'var(--text-3)', fontSize:12, marginTop:6 }}>Account #{company.account_num}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ Submit Wrap Order</button>
      </div>

      <QuoteBanner />

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:28 }}>
        {[
          { l:'Active Orders', v:active.length,                                        i:'⚡', c:'var(--accent)' },
          { l:'In Production', v:orders.filter(o=>o.status==='In Production').length,  i:'⚙️', c:'var(--success)' },
          { l:'Proof Sent',    v:orders.filter(o=>o.status==='Proof Sent').length,     i:'📧', c:'#8b5cf6' },
          { l:'Completed',     v:orders.filter(o=>o.status==='Completed').length,      i:'✓',  c:'var(--text-2)' },
        ].map(s => (
          <div key={s.l} className="stat">
            <div style={{ fontSize:20, marginBottom:8, opacity:.8 }}>{s.i}</div>
            <div className="bc" style={{ fontSize:42, fontWeight:900, lineHeight:1, color:s.c }}>{s.v}</div>
            <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginTop:6 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Active orders card */}
      <div className="card" style={{ marginBottom:14 }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(0,0,0,.15)' }}>
          <div className="bc" style={{ fontSize:14, fontWeight:800, letterSpacing:'2px', textTransform:'uppercase' }}>Active Orders</div>
          <button className="btn btn-ghost btn-sm" onClick={() => setView('orders')}>View All →</button>
        </div>
        {active.length === 0
          ? <div style={{ padding:'50px', textAlign:'center', color:'var(--text-3)', fontSize:14 }}>No active orders — submit one above.</div>
          : active.slice(0,6).map(o => (
            <div key={o.id} className="orow" onClick={() => setSelOrder(o)}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                <div>
                  <div className="bc" style={{ fontWeight:700, fontSize:16, color:'#fff' }}>{o.vehicle}</div>
                  <div style={{ color:'var(--text-2)', fontSize:11, marginTop:3 }}>{o.id} · {o.size} · {o.laminate} Lam</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <StatusPill status={o.status} />
                  <div style={{ color:'var(--text-3)', fontSize:11, marginTop:4 }}>{o.created_at?.split('T')[0]}</div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* File requirements banner */}
      <div style={{ background:'var(--accent-dim)', border:'1px solid rgba(0,159,255,0.12)', borderRadius:'var(--r)', padding:'14px 20px', display:'flex', gap:14, alignItems:'flex-start' }}>
        <span style={{ fontSize:18, flexShrink:0 }}>📋</span>
        <div>
          <div className="bc" style={{ fontWeight:700, fontSize:12, letterSpacing:'2px', color:'var(--accent)', marginBottom:3 }}>FILE REQUIREMENTS</div>
          <div style={{ color:'var(--text-2)', fontSize:12, lineHeight:'1.7' }}>
            Print-ready PDF · 100% scale · 1.5" bleed min · CMYK · 150 DPI min · Max 500MB/file
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'orders') return wrap(
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div className="bc" style={{ fontSize:32, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase' }}>All Orders</div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ Submit Wrap Order</button>
      </div>
      <QuoteBanner />
      <div className="card">
        <table>
          <thead>
            <tr><th>Order ID</th><th>Vehicle</th><th>Specs</th><th>Files</th><th>Status</th><th>Date</th><th></th></tr>
          </thead>
          <tbody>
            {orders.length === 0
              ? <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--text-3)', padding:60 }}>No orders yet.</td></tr>
              : orders.map(o => (
                <tr key={o.id} className="click" onClick={() => setSelOrder(o)}>
                  <td style={{ color:'var(--text-2)', fontSize:11, fontFamily:"'Space Grotesk',sans-serif", letterSpacing:'1px' }}>{o.id}</td>
                  <td className="bc" style={{ fontWeight:700, fontSize:16, color:'#fff' }}>{o.vehicle}</td>
                  <td style={{ color:'var(--text-2)', fontSize:12 }}>{o.size}<br /><span style={{ color:'var(--text-3)' }}>{o.material?.split(' ').slice(0,3).join(' ')} · {o.laminate}</span></td>
                  <td>
                    <span style={{ color: o.files?.length ? 'var(--success)' : 'var(--warn)', fontSize:12, fontWeight:600 }}>
                      {o.files?.length || 0} file{o.files?.length !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td><StatusPill status={o.status} /></td>
                  <td style={{ color:'var(--text-2)', fontSize:12 }}>{o.created_at?.split('T')[0]}</td>
                  <td onClick={e => e.stopPropagation()} style={{ whiteSpace:'nowrap' }}>
                    {o.status === 'Completed' && (
                      <button className="btn btn-ghost btn-sm" style={{ marginRight:6 }} onClick={() => setReorderOrder(o)}>Reorder</button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => setPayOrder(o)}>Pay</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return wrap(
    <div style={{ maxWidth:560 }}>
      <div className="bc" style={{ fontSize:32, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', marginBottom:24 }}>Account</div>
      <div className="card" style={{ marginBottom:12 }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)' }}>
          <div className="section-label" style={{ margin:0 }}>Company Info</div>
        </div>
        <div style={{ padding:'0 20px' }}>
          {[['Company',company.company],['Account #',company.account_num],['Contact',company.contact],['Email',company.email],['Phone',company.phone]].map(([k,v]) => (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'13px 0', borderBottom:'1px solid var(--border-sub)', fontSize:13 }}>
              <span style={{ color:'var(--text-2)' }}>{k}</span>
              <span style={{ color:'var(--text)', fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Saved Vehicles */}
      <div className="card" style={{ marginBottom:12 }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)' }}>
          <div className="section-label" style={{ margin:0 }}>Saved Vehicles</div>
        </div>
        <div style={{ padding:20 }}>
          {savedVehiclesList.length === 0
            ? <div style={{ color:'var(--text-3)', fontSize:13, marginBottom:16 }}>No saved vehicles yet. Save a vehicle when submitting an order for quick reordering.</div>
            : (
              <div style={{ marginBottom:16 }}>
                {savedVehiclesList.map(v => (
                  <div key={v.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid var(--border-sub)', fontSize:13 }}>
                    <span style={{ color:'var(--text)', fontWeight:500 }}>🚗 {v.name}</span>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={async () => {
                        await supabase.from('saved_vehicles').delete().eq('id', v.id);
                        setSavedVehiclesList(p => p.filter(x => x.id !== v.id));
                        notify('Vehicle removed.');
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )
          }
          <div style={{ display:'flex', gap:8 }}>
            <input
              className="finput"
              style={{ flex:1 }}
              placeholder="Add a vehicle name…"
              value={newVehicleName}
              onChange={e => setNewVehicleName(e.target.value)}
              onKeyDown={async e => {
                if (e.key === 'Enter' && newVehicleName.trim()) {
                  const { data } = await supabase.from('saved_vehicles').insert({ company_id: company.id, name: newVehicleName.trim() }).select().single();
                  if (data) setSavedVehiclesList(p => [...p, data].sort((a,b) => a.name.localeCompare(b.name)));
                  setNewVehicleName('');
                  notify('Vehicle saved!');
                }
              }}
            />
            <button
              className="btn btn-ghost"
              onClick={async () => {
                if (!newVehicleName.trim()) return;
                const { data } = await supabase.from('saved_vehicles').insert({ company_id: company.id, name: newVehicleName.trim() }).select().single();
                if (data) setSavedVehiclesList(p => [...p, data].sort((a,b) => a.name.localeCompare(b.name)));
                setNewVehicleName('');
                notify('Vehicle saved!');
              }}
            >
              + Add
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)' }}>
          <div className="section-label" style={{ margin:0 }}>Wholesale Terms</div>
        </div>
        <div style={{ padding:20 }}>
          <div style={{ color:'var(--text-2)', fontSize:13, lineHeight:'2' }}>
            Contract printing partner · Print-ready PDF required<br />
            Net-30 payment terms · Rush orders available<br />
            Minimum 1 wrap set · File corrections: $85/hr
          </div>
          <div style={{ marginTop:18, background:'var(--bg-elevated)', padding:16, borderRadius:'var(--r-sm)', borderLeft:'3px solid var(--accent)' }}>
            <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginBottom:6 }}>Your Account Rep</div>
            <div style={{ color:'#fff', fontWeight:600, fontSize:15 }}>Broc Braunworth</div>
            <div style={{ color:'var(--text-2)', fontSize:12, marginTop:2 }}>sales@ultimateprintingco.com · 952-913-2547</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── COMPANY DRAWER ───────────────────────────────────────────────────────────
function CompanyDrawer({ company, orders, onClose, onEdit }) {
  const [expanded,     setExpanded]     = useState(null);
  const [downloading,  setDownloading]  = useState(null);
  const [drawerTab,    setDrawerTab]    = useState('orders'); // 'orders' | 'pricing'
  const [products,     setProducts]     = useState([]);
  const [overrides,    setOverrides]    = useState({}); // { product_id: price_string }
  const [savingPrices, setSavingPrices] = useState(false);
  const [priceMsg,     setPriceMsg]     = useState('');

  const coOrders   = orders.filter(o => o.company_id === company.id);
  const activeCount = coOrders.filter(o => o.status !== 'Completed').length;
  const fileCount   = coOrders.reduce((s, o) => s + (o.files?.length || 0), 0);

  useEffect(() => {
    const load = async () => {
      const [{ data: prods }, { data: ovs }] = await Promise.all([
        supabase.from('products').select('*').eq('is_active', true).order('name', { ascending: true }),
        supabase.from('company_product_overrides').select('*').eq('company_id', company.id),
      ]);
      setProducts(prods || []);
      const map = {};
      (ovs || []).forEach(o => { map[o.product_id] = String(o.price_per_linear_foot); });
      setOverrides(map);
    };
    load();
  }, [company.id]);

  const savePricing = async () => {
    setSavingPrices(true); setPriceMsg('');
    const toInsert = Object.entries(overrides)
      .filter(([, v]) => v !== '' && !isNaN(Number(v)))
      .map(([product_id, price]) => ({ company_id: company.id, product_id, price_per_linear_foot: Number(price) }));
    await supabase.from('company_product_overrides').delete().eq('company_id', company.id);
    if (toInsert.length > 0) await supabase.from('company_product_overrides').insert(toInsert);
    setSavingPrices(false);
    setPriceMsg('Pricing saved!');
    setTimeout(() => setPriceMsg(''), 2500);
  };

  const downloadFile = async (file) => {
    setDownloading(file.id);
    const { data } = await supabase.storage.from('wrap-files').createSignedUrl(file.file_path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    setDownloading(null);
  };

  const toggle = (id) => setExpanded(e => e === id ? null : id);

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:820 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:'22px 26px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'flex-start', justifyContent:'space-between', background:'rgba(0,0,0,.2)' }}>
          <div>
            <div style={{ color:'var(--accent)', fontSize:10, fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', marginBottom:5 }}>Partner Account</div>
            <div className="bc" style={{ fontSize:26, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff', lineHeight:1.1 }}>{company.company}</div>
            <div style={{ color:'var(--text-2)', fontSize:12, marginTop:5 }}>
              {company.account_num} · {company.email}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { onClose(); onEdit(company); }}>Edit Company</button>
            <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:22, cursor:'pointer', padding:'2px 6px' }}>×</button>
          </div>
        </div>

        {/* Drawer tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)' }}>
          {[['orders','Orders'],['pricing','Pricing']].map(([t,l]) => (
            <button key={t} onClick={() => setDrawerTab(t)} style={{
              background:'none', border:'none', borderBottom: drawerTab===t ? '2px solid var(--accent)' : '2px solid transparent',
              color: drawerTab===t ? 'var(--accent)' : 'var(--text-2)',
              padding:'10px 22px', cursor:'pointer', fontFamily:"'Space Grotesk',sans-serif",
              fontWeight:700, fontSize:13, letterSpacing:'2px', textTransform:'uppercase', transition:'all .15s',
            }}>{l}</button>
          ))}
        </div>

        {/* Stats strip */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', borderBottom:'1px solid var(--border)' }}>
          {[
            { l:'Total Orders', v:coOrders.length,                                              c:'var(--text)' },
            { l:'Active',       v:activeCount,                                                  c:'var(--accent)' },
            { l:'Completed',    v:coOrders.filter(o=>o.status==='Completed').length,            c:'var(--text-2)' },
            { l:'Total Files',  v:fileCount,                                                    c:'var(--success)' },
          ].map((s,i,arr) => (
            <div key={s.l} style={{ padding:'16px 20px', borderRight: i < arr.length-1 ? '1px solid var(--border)' : 'none', background:'rgba(0,0,0,.15)' }}>
              <div className="bc" style={{ fontSize:28, fontWeight:900, color:s.c, lineHeight:1 }}>{s.v}</div>
              <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginTop:4 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Pricing panel */}
        {drawerTab === 'pricing' && (
          <div style={{ padding:24, maxHeight:'60vh', overflowY:'auto' }}>
            <div style={{ marginBottom:18 }}>
              <div className="bc" style={{ fontSize:16, fontWeight:700, textTransform:'uppercase', letterSpacing:'1px', color:'#fff', marginBottom:4 }}>
                Price Per Linear Foot — Custom Rates
              </div>
              <div style={{ color:'var(--text-2)', fontSize:12 }}>
                Leave blank to use the product's base price. Enter a custom $/linear ft rate for this partner.
              </div>
            </div>
            {products.length === 0 ? (
              <div style={{ color:'var(--text-3)', fontSize:13, padding:'24px 0' }}>No active products found. Add products in the Products tab first.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
                {products.map(p => (
                  <div key={p.id} style={{ display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap:14, background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', padding:'12px 16px' }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:14, color:'#fff' }}>{p.name}</div>
                      <div style={{ color:'var(--text-2)', fontSize:11, marginTop:2 }}>Base: ${Number(p.base_price).toFixed(2)}/ft</div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ color:'var(--text-2)', fontSize:12 }}>$</span>
                      <input
                        type="number" min="0" step="0.01"
                        placeholder={p.base_price}
                        value={overrides[p.id] ?? ''}
                        onChange={e => setOverrides(prev => ({ ...prev, [p.id]: e.target.value }))}
                        style={{ width:90, background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', color:'var(--text)', padding:'7px 10px', fontSize:14, fontFamily:"'Space Grotesk',sans-serif", fontWeight:600 }}
                      />
                      <span style={{ color:'var(--text-2)', fontSize:12 }}>/ft</span>
                    </div>
                    {overrides[p.id] ? (
                      <span style={{ color:'var(--accent)', fontSize:11, fontWeight:700, letterSpacing:'1px' }}>CUSTOM</span>
                    ) : (
                      <span style={{ color:'var(--text-3)', fontSize:11 }}>BASE</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <button className="btn btn-primary" onClick={savePricing} disabled={savingPrices}>
                {savingPrices ? 'Saving…' : 'Save Pricing'}
              </button>
              {priceMsg && <span style={{ color:'var(--success)', fontSize:13, fontWeight:600 }}>✓ {priceMsg}</span>}
            </div>
          </div>
        )}

        {/* Orders list */}
        {drawerTab === 'orders' && <div style={{ maxHeight:'60vh', overflowY:'auto' }}>
          {coOrders.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center', color:'var(--text-3)', fontSize:14 }}>No orders yet for this company.</div>
          ) : coOrders.map(order => {
            const isOpen = expanded === order.id;
            const sc = STATUS_CFG[order.status] || {};
            return (
              <div key={order.id} style={{ borderBottom:'1px solid var(--border-sub)' }}>
                {/* Order row */}
                <div
                  onClick={() => toggle(order.id)}
                  style={{ padding:'14px 20px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, transition:'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}
                >
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                      <div className="bc" style={{ fontWeight:700, fontSize:15, color:'#fff' }}>{order.vehicle}</div>
                      <StatusPill status={order.status} />
                    </div>
                    <div style={{ color:'var(--text-2)', fontSize:11 }}>
                      {order.id} · {order.size} · {order.laminate} · Qty {order.quantity} · {order.created_at?.split('T')[0]}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
                    <span style={{ color: order.files?.length ? 'var(--success)' : 'var(--text-3)', fontSize:12, fontWeight:600 }}>
                      {order.files?.length || 0} file{order.files?.length !== 1 ? 's' : ''}
                    </span>
                    <span style={{ color:'var(--text-2)', fontSize:13, transition:'transform .2s', display:'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
                  </div>
                </div>

                {/* Expanded files */}
                {isOpen && (
                  <div style={{ background:'var(--bg-elevated)', borderTop:'1px solid var(--border-sub)', padding:'16px 20px' }}>
                    {order.notes && (
                      <div style={{ marginBottom:14, padding:'10px 14px', background:'var(--bg-input)', borderRadius:'var(--r-sm)', borderLeft:'2px solid var(--accent)', fontSize:12, color:'var(--text-2)' }}>
                        <span style={{ color:'var(--text-2)', fontWeight:700, marginRight:6 }}>Notes:</span>{order.notes}
                      </div>
                    )}
                    {order.admin_notes && (
                      <div style={{ marginBottom:14, padding:'10px 14px', background:'var(--bg-input)', borderRadius:'var(--r-sm)', borderLeft:'2px solid var(--warn)', fontSize:12, color:'var(--text-2)' }}>
                        <span style={{ color:'var(--warn)', fontWeight:700, marginRight:6 }}>Admin Notes:</span>{order.admin_notes}
                      </div>
                    )}
                    {(!order.files || order.files.length === 0) ? (
                      <div style={{ color:'var(--warn)', fontSize:12 }}>⏳ No files uploaded yet</div>
                    ) : (
                      <div>
                        <div className="flabel" style={{ marginBottom:10 }}>Files</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                          {order.files.map(f => (
                            <div key={f.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', padding:'10px 14px' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <span style={{ color:'var(--accent)', fontSize:14 }}>
                                  {f.file_name.toLowerCase().endsWith('.eps') ? '📐' : '📄'}
                                </span>
                                <div>
                                  <div style={{ color:'var(--text)', fontSize:13, fontWeight:500 }}>{f.file_name}</div>
                                  {f.file_size && <div style={{ color:'var(--text-2)', fontSize:11, marginTop:1 }}>{(f.file_size/1024/1024).toFixed(1)} MB</div>}
                                </div>
                              </div>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => downloadFile(f)}
                                disabled={downloading === f.id}
                                style={{ flexShrink:0 }}
                              >
                                {downloading === f.id ? 'Opening…' : '↓ Download'}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>}
      </div>
    </div>
  );
}

// ─── PRODUCT MODAL (admin) ───────────────────────────────────────────────────
const PRODUCT_CATEGORIES = ['Banners','Business Cards','Flyers','Posters','Stickers','Apparel','Signage','Race Wraps','Other'];

function ProductModal({ product, onClose, onSave, onDelete }) {
  const isEdit = !!product;
  const [form, setForm] = useState(product
    ? { ...product }
    : { name:'', description:'', category:'Race Wraps', base_price:'', image_url:'', is_active:true });
  const [saving,     setSaving]     = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [error,      setError]      = useState('');
  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const handleSave = async () => {
    if (!form.name.trim())                          { setError('Product name is required.'); return; }
    if (form.base_price === '' || isNaN(Number(form.base_price))) { setError('A valid base price is required.'); return; }
    setSaving(true); setError('');
    try { await onSave({ ...form, base_price: Number(form.base_price) }); }
    catch(e) { setError(e.message); setSaving(false); }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ padding:'22px 26px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(0,0,0,.2)' }}>
          <div className="bc" style={{ fontSize:20, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff' }}>
            {isEdit ? 'Edit Product' : 'New Product'}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:22, cursor:'pointer', padding:'2px 6px', borderRadius:4 }}>×</button>
        </div>
        <div style={{ padding:26 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div style={{ gridColumn:'1/-1' }}><FInput label="Product Name *" value={form.name} onChange={e => set('name', e.target.value)} /></div>
            <FSelect label="Category" opts={PRODUCT_CATEGORIES} value={form.category||''} onChange={e => set('category', e.target.value)} />
            <FInput label="Base Price ($) *" type="number" min="0" step="0.01" value={form.base_price} onChange={e => set('base_price', e.target.value)} />
            <div style={{ gridColumn:'1/-1' }}><FInput label="Image URL" value={form.image_url||''} onChange={e => set('image_url', e.target.value)} /></div>
            <div style={{ gridColumn:'1/-1' }}>
              <label className="flabel">Description</label>
              <textarea className="finput" rows={3} style={{ resize:'vertical', marginBottom:16 }} placeholder="Optional description…" value={form.description||''} onChange={e => set('description', e.target.value)} />
            </div>
            <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:10 }}>
              <span className="flabel" style={{ margin:0 }}>Status</span>
              <button
                onClick={() => set('is_active', !form.is_active)}
                style={{
                  background: form.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                  border: `1px solid ${form.is_active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  color: form.is_active ? 'var(--success)' : 'var(--danger)',
                  padding:'6px 16px', fontFamily:"'Space Grotesk',sans-serif",
                  fontSize:12, fontWeight:700, letterSpacing:'1px', textTransform:'uppercase',
                  cursor:'pointer', borderRadius:'var(--r-sm)',
                }}
              >
                {form.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
              </button>
            </div>
          </div>
          {error && <div style={{ color:'var(--danger)', fontSize:13, margin:'12px 0', display:'flex', gap:6 }}><span>⚠</span>{error}</div>}
          <div className="divider" />
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {isEdit && (!confirmDel
              ? <button className="btn btn-danger" onClick={() => setConfirmDel(true)}>Delete</button>
              : <button className="btn btn-danger-confirm" onClick={() => onDelete(form.id)}>Confirm Delete?</button>
            )}
            <div style={{ flex:1 }} />
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN PORTAL ─────────────────────────────────────────────────────────────
function AdminPortal({ onLogout, theme, onThemeToggle }) {
  const [view,      setView]      = useState('dashboard');
  const [orders,    setOrders]    = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selOrder,  setSelOrder]  = useState(null);
  const [selCompany, setSelCompany] = useState(null);
  const [editCo,    setEditCo]    = useState(null);
  const [showNewCo, setShowNewCo] = useState(false);
  const [showNewProd, setShowNewProd] = useState(false);
  const [editProd,    setEditProd]    = useState(null);
  const [search,    setSearch]    = useState('');
  const [fStatus,   setFStatus]   = useState('All');
  const [fCo,       setFCo]       = useState('All');
  const [notif,          setNotif]          = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkStatus,     setBulkStatus]     = useState('');
  const [fThisMonth,     setFThisMonth]     = useState(false);
  const [calMonth,       setCalMonth]       = useState(new Date());
  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [calSelectedDay, setCalSelectedDay] = useState(null);
  const notify = (msg, type='success') => { setNotif({ msg, type }); setTimeout(() => setNotif(null), 3500); };

  const loadAll = useCallback(async () => {
    const [{ data:os }, { data:cs }, { data:ps }] = await Promise.all([
      supabase.from('orders').select('*, files:order_files(*), payments:payments(*)').order('created_at', { ascending:false }),
      supabase.from('companies').select('*').order('created_at', { ascending:true }),
      supabase.from('products').select('*').order('name', { ascending:true }),
    ]);
    setOrders(os || []);
    setCompanies(cs || []);
    setProducts(ps || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateOrder = async (updated) => {
    const oldOrder = orders.find(o => o.id === updated.id);
    const { error } = await supabase.from('orders').update({
      status:          updated.status,
      admin_notes:     updated.admin_notes,
      tracking_number: updated.tracking_number,
    }).eq('id', updated.id);
    if (error) { notify('Save failed.', 'error'); return; }
    if (oldOrder?.status !== updated.status) {
      const co = companies.find(c => c.id === updated.company_id);
      if (co?.email) {
        try {
          await supabase.functions.invoke('send-status-email', {
            body: { order_id: updated.id, new_status: updated.status, partner_email: co.email, order_vehicle: updated.vehicle },
          });
        } catch {}
      }
    }
    await loadAll();
    notify('Order updated.');
  };

  const bulkApply = async () => {
    if (!bulkStatus || selectedOrders.size === 0) return;
    const ids = [...selectedOrders];
    await Promise.all(ids.map(async (id) => {
      const order = orders.find(o => o.id === id);
      if (order?.status === bulkStatus) return;
      await supabase.from('orders').update({ status: bulkStatus }).eq('id', id);
      const co = companies.find(c => c.id === order?.company_id);
      if (co?.email) {
        try {
          await supabase.functions.invoke('send-status-email', {
            body: { order_id: id, new_status: bulkStatus, partner_email: co.email, order_vehicle: order?.vehicle },
          });
        } catch {}
      }
    }));
    setSelectedOrders(new Set());
    setBulkStatus('');
    await loadAll();
    notify(`${ids.length} order${ids.length > 1 ? 's' : ''} updated to ${bulkStatus}.`);
  };

  const addCompany = async (form) => {
    // Create the Supabase Auth login first
    const { error: authErr } = await createAuthUser(form.email, form.newPassword);
    if (authErr && !authErr.message.includes('already registered')) throw authErr;

    const { error } = await supabase.from('companies').insert({
      company:      form.company,
      email:        form.email,
      contact:      form.contact,
      phone:        form.phone,
      discount_pct: form.discount_pct || 0,
      account_num: form.account_num,
      active:      form.active,
      notes:       form.notes,
    });
    if (error) throw error;
    await loadAll();
    notify(`${form.company} added — login created!`);
  };

  const saveCompany = async (form) => {
    const { error } = await supabase.from('companies').update({
      company:      form.company,
      contact:      form.contact,
      phone:        form.phone,
      account_num:  form.account_num,
      active:       form.active,
      notes:        form.notes,
      discount_pct: form.discount_pct || 0,
    }).eq('id', form.id);
    if (error) throw error;
    await loadAll();
    notify(`${form.company} updated.`);
  };

  const sendPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) { notify('Reset email failed.', 'error'); return; }
    notify(`Password reset email sent to ${email}.`);
  };

  const deleteCompany = async (id) => {
    const { error } = await supabase.from('companies').delete().eq('id', id);
    if (error) { notify('Delete failed.', 'error'); return; }
    await loadAll();
    notify('Company removed.', 'warn');
  };

  const addProduct = async (form) => {
    const { error } = await supabase.from('products').insert({
      name:        form.name,
      description: form.description,
      category:    form.category,
      base_price:  form.base_price,
      image_url:   form.image_url,
      is_active:   form.is_active,
    });
    if (error) throw error;
    await loadAll();
    notify(`${form.name} added.`);
  };

  const saveProduct = async (form) => {
    const { error } = await supabase.from('products').update({
      name:        form.name,
      description: form.description,
      category:    form.category,
      base_price:  form.base_price,
      image_url:   form.image_url,
      is_active:   form.is_active,
    }).eq('id', form.id);
    if (error) throw error;
    await loadAll();
    notify(`${form.name} updated.`);
  };

  const deleteProduct = async (id) => {
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) { notify('Delete failed.', 'error'); return; }
    await loadAll();
    notify('Product removed.', 'warn');
  };

  const NEEDS_ATTN_STATUSES = ['Pending Review','Awaiting Files','Proof Approved'];
  const filtered = orders.filter(o => {
    if (fStatus === 'Needs Attention' && !NEEDS_ATTN_STATUSES.includes(o.status)) return false;
    if (fStatus !== 'All' && fStatus !== 'Needs Attention' && o.status !== fStatus) return false;
    if (fCo !== 'All' && o.company_id !== fCo) return false;
    if (fThisMonth) {
      const d = new Date(o.created_at);
      if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
    }
    if (search && !o.vehicle.toLowerCase().includes(search.toLowerCase()) && !o.id.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const needsAttn = orders.filter(o => ['Pending Review','Awaiting Files','Proof Approved'].includes(o.status));
  const inProd    = orders.filter(o => o.status === 'In Production');
  const readyShip = orders.filter(o => o.status === 'Ready to Ship');
  const coOf      = (id) => companies.find(c => c.id === id);

  const confirmedRevenue = orders.reduce((sum, o) =>
    sum + (o.payments || []).filter(p => p.status === 'confirmed').reduce((s, p) => s + Number(p.amount), 0), 0);
  const now = new Date();
  const thisMonthOrders = orders.filter(o => {
    const d = new Date(o.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const lastMonthOrders = orders.filter(o => {
    const d = new Date(o.created_at);
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return d.getMonth() === lm.getMonth() && d.getFullYear() === lm.getFullYear();
  });
  const topCompanies = [...companies]
    .map(c => ({ ...c, orderCount: orders.filter(o => o.company_id === c.id).length }))
    .sort((a, b) => b.orderCount - a.orderCount)
    .slice(0, 3);

  const toggleAllFiltered = (filtered) => {
    const allSelected = filtered.every(o => selectedOrders.has(o.id));
    setSelectedOrders(prev => {
      const next = new Set(prev);
      filtered.forEach(o => allSelected ? next.delete(o.id) : next.add(o.id));
      return next;
    });
  };
  const toggleOne = (id) => setSelectedOrders(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const navOrders = (status = 'All', thisMonth = false) => {
    setFStatus(status);
    setFThisMonth(thisMonth);
    setFCo('All');
    setSearch('');
    setView('orders');
  };

  const wrap = (content) => (
    <div className="main-layout" style={{ background:'var(--bg)', color:'var(--text)' }}>
      <style>{CSS}</style>
      <Sidebar
        items={[
          ['dashboard', 'Dashboard', Icons.dashboard],
          ['orders',    'All Orders', Icons.orders],
          ['companies', 'Companies', Icons.companies],
          ['products',  'Products',  Icons.products],
          ['calendar',  'Calendar',  Icons.calendar],
        ]}
        active={view} onNav={setView} onLogout={onLogout}
        badge="Admin" theme={theme} onThemeToggle={onThemeToggle}
        userEmail={ADMIN_EMAIL}
        open={sidebarOpen} onClose={() => setSidebarOpen(false)}
      />
      <div className="main-content">
        <div className="mobile-bar">
          <button className="hamburger" onClick={() => setSidebarOpen(true)}><span/><span/><span/></button>
          <Logo height={22} />
          <div style={{ width:36 }} />
        </div>
        <Notif n={notif} />
        <div className="main-inner">
          {loading ? <div style={{ color:'var(--text-3)', textAlign:'center', paddingTop:100, fontSize:13 }}>Loading…</div> : content}
        </div>
      </div>
      {selOrder   && <OrderModal order={selOrder} company={coOf(selOrder.company_id)} onClose={() => setSelOrder(null)} onUpdate={async u => { await updateOrder(u); setSelOrder(null); }} isAdmin={true} senderEmail={ADMIN_EMAIL} senderRole="admin" />}
      {selCompany && <CompanyDrawer company={selCompany} orders={orders} onClose={() => setSelCompany(null)} onEdit={co => { setSelCompany(null); setEditCo(co); }} />}
      {showNewCo  && <CompanyModal onClose={() => setShowNewCo(false)} onSave={async f => { await addCompany(f); setShowNewCo(false); }} existingCount={companies.length} />}
      {editCo     && <CompanyModal company={editCo} onClose={() => setEditCo(null)} onSave={async f => { await saveCompany(f); setEditCo(null); }} onDelete={async id => { await deleteCompany(id); setEditCo(null); }} onPasswordReset={sendPasswordReset} existingCount={companies.length} />}
      {showNewProd && <ProductModal onClose={() => setShowNewProd(false)} onSave={async f => { await addProduct(f); setShowNewProd(false); }} />}
      {editProd    && <ProductModal product={editProd} onClose={() => setEditProd(null)} onSave={async f => { await saveProduct(f); setEditProd(null); }} onDelete={async id => { await deleteProduct(id); setEditProd(null); }} />}
    </div>
  );

  if (view === 'dashboard') return wrap(
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:32, flexWrap:'wrap', gap:14 }}>
        <div>
          <div style={{ color:'var(--text-2)', fontSize:11, fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', marginBottom:6 }}>Admin</div>
          <div className="bc" style={{ fontSize:36, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff' }}>Production Overview</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:16 }}>
        {[
          { l:'Total Orders',    v:orders.length,                                        c:'var(--text)',    i:'📋', fn:() => navOrders() },
          { l:'Active',          v:orders.filter(o=>o.status!=='Completed').length,      c:'var(--accent)',  i:'⚡', fn:() => { setFStatus('All'); setFThisMonth(false); setFCo('All'); setSearch(''); setView('orders'); } },
          { l:'Needs Attention', v:needsAttn.length,                                     c:'var(--warn)',    i:'⚠', fn:() => navOrders('Needs Attention') },
          { l:'In Production',   v:inProd.length,                                        c:'var(--success)', i:'⚙️', fn:() => navOrders('In Production') },
          { l:'Ready to Ship',   v:readyShip.length,                                     c:'#f97316',        i:'📦', fn:() => navOrders('Ready to Ship') },
          { l:'Partners',        v:companies.length,                                     c:'#8b5cf6',        i:'🏢', fn:() => setView('companies') },
        ].map(s => (
          <div key={s.l} className="stat" onClick={s.fn} style={{ cursor:'pointer' }}>
            <div style={{ fontSize:20, marginBottom:8, opacity:.7 }}>{s.i}</div>
            <div className="bc" style={{ fontSize:40, fontWeight:900, lineHeight:1, color:s.c }}>{s.v}</div>
            <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginTop:6 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Revenue + Volume row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12, marginBottom:28 }}>
        <div className="stat" style={{ borderTop:'2px solid rgba(16,185,129,0.3)', cursor:'pointer' }} onClick={() => navOrders()}>
          <div style={{ fontSize:18, marginBottom:6, opacity:.7 }}>💰</div>
          <div className="bc" style={{ fontSize:32, fontWeight:900, lineHeight:1, color:'var(--success)' }}>
            ${confirmedRevenue.toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}
          </div>
          <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginTop:6 }}>Confirmed Revenue</div>
        </div>
        <div className="stat" style={{ borderTop:'2px solid rgba(0,159,255,0.3)', cursor:'pointer' }} onClick={() => navOrders('All', true)}>
          <div style={{ fontSize:18, marginBottom:6, opacity:.7 }}>📅</div>
          <div style={{ display:'flex', alignItems:'baseline', gap:10 }}>
            <div className="bc" style={{ fontSize:32, fontWeight:900, lineHeight:1, color:'var(--accent)' }}>{thisMonthOrders.length}</div>
            {lastMonthOrders.length > 0 && (
              <div style={{ fontSize:11, color: thisMonthOrders.length >= lastMonthOrders.length ? 'var(--success)' : 'var(--danger)', fontWeight:600 }}>
                {thisMonthOrders.length >= lastMonthOrders.length ? '▲' : '▼'} vs {lastMonthOrders.length} last mo
              </div>
            )}
          </div>
          <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginTop:6 }}>This Month</div>
        </div>
        <div className="stat" style={{ borderTop:'2px solid rgba(139,92,246,0.3)', cursor:'pointer' }} onClick={() => setView('companies')}>
          <div style={{ fontSize:18, marginBottom:6, opacity:.7 }}>🏆</div>
          {topCompanies.length === 0
            ? <div style={{ color:'var(--text-3)', fontSize:13 }}>No data</div>
            : topCompanies.map((c, i) => (
              <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:3 }}>
                <div style={{ color: i === 0 ? '#fff' : 'var(--text-2)', fontSize:12, fontWeight: i === 0 ? 600 : 400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {c.company}
                </div>
                <div style={{ color:'var(--text-2)', fontSize:11 }}>{c.orderCount}</div>
              </div>
            ))}
          <div style={{ color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginTop:6 }}>Top Partners</div>
        </div>
      </div>

      {/* Two-col boards */}
      <div className="grid-2col">
        {[
          { title:'Needs Attention', count:needsAttn.length, rows:needsAttn, c:'var(--warn)',    icon:'⚠' },
          { title:'Ready to Ship',   count:readyShip.length, rows:readyShip, c:'#f97316',        icon:'📦' },
        ].map(({ title, count, rows, c, icon }) => (
          <div key={title} className="card">
            <div style={{ padding:'15px 20px', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)' }}>
              <div className="bc" style={{ fontSize:13, fontWeight:800, letterSpacing:'2px', textTransform:'uppercase', color:c }}>
                {icon} {title} <span style={{ color:'var(--text-2)', fontWeight:400 }}>({count})</span>
              </div>
            </div>
            {rows.length === 0
              ? <div style={{ padding:'28px 20px', color:'var(--text-3)', fontSize:13 }}>All clear</div>
              : rows.map(o => (
                <div key={o.id} className="orow" onClick={() => setSelOrder(o)}>
                  <div className="bc" style={{ fontWeight:700, fontSize:15, color:'#fff', marginBottom:4 }}>{o.vehicle}</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ color:'var(--text-2)', fontSize:12 }}>{coOf(o.company_id)?.company}</span>
                    <StatusPill status={o.status} />
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>

      {/* In production */}
      <div className="card">
        <div style={{ padding:'15px 20px', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)' }}>
          <div className="bc" style={{ fontSize:13, fontWeight:800, letterSpacing:'2px', textTransform:'uppercase', color:'var(--success)' }}>
            ⚙️ In Production <span style={{ color:'var(--text-2)', fontWeight:400 }}>({inProd.length})</span>
          </div>
        </div>
        {inProd.length === 0
          ? <div style={{ padding:'28px 20px', color:'var(--text-3)', fontSize:13 }}>Nothing in production</div>
          : inProd.map(o => (
            <div key={o.id} className="orow" onClick={() => setSelOrder(o)}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                <div>
                  <div className="bc" style={{ fontWeight:700, fontSize:15, color:'#fff', marginBottom:3 }}>{o.vehicle}</div>
                  <div style={{ color:'var(--text-2)', fontSize:12 }}>{o.id} · {coOf(o.company_id)?.company} · {o.size}</div>
                  {o.admin_notes && (
                    <div style={{ color:'var(--text-3)', fontSize:11, marginTop:4 }}>
                      📝 {o.admin_notes.slice(0,90)}{o.admin_notes.length > 90 ? '…' : ''}
                    </div>
                  )}
                </div>
                <StatusPill status={o.status} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );

  if (view === 'orders') return wrap(
    <div>
      <div className="bc" style={{ fontSize:32, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', marginBottom:20 }}>All Orders</div>
      <div style={{ display:'flex', gap:10, marginBottom:fThisMonth ? 8 : 16, flexWrap:'wrap' }}>
        <input
          className="finput"
          placeholder="Search vehicle or order ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex:1, minWidth:160, maxWidth:260 }}
        />
        <select className="fselect" value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ maxWidth:190 }}>
          <option value="All">All Statuses</option>
          <option value="Needs Attention">⚠ Needs Attention</option>
          {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="fselect" value={fCo} onChange={e => setFCo(e.target.value)} style={{ maxWidth:210 }}>
          <option value="All">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.company}</option>)}
        </select>
      </div>
      {fThisMonth && (
        <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:12, padding:'6px 12px', background:'rgba(0,159,255,0.1)', border:'1px solid rgba(0,159,255,0.25)', borderRadius:'var(--r-sm)', fontSize:12 }}>
          <span style={{ color:'var(--accent)', fontWeight:700 }}>📅 This Month</span>
          <button onClick={() => setFThisMonth(false)} style={{ background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', fontSize:14, lineHeight:1, padding:0 }}>×</button>
        </div>
      )}
      {selectedOrders.size > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, padding:'12px 16px', background:'var(--accent-dim)', border:'1px solid rgba(0,159,255,0.2)', borderRadius:'var(--r-sm)', flexWrap:'wrap' }}>
          <span style={{ color:'var(--accent)', fontSize:12, fontWeight:700, letterSpacing:'1px' }}>
            {selectedOrders.size} selected
          </span>
          <select className="fselect" value={bulkStatus} onChange={e => setBulkStatus(e.target.value)} style={{ maxWidth:200, padding:'7px 12px', fontSize:12 }}>
            <option value="">Set status…</option>
            {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={bulkApply} disabled={!bulkStatus}>Apply</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedOrders(new Set())}>Clear</button>
        </div>
      )}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th style={{ width:44 }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every(o => selectedOrders.has(o.id))}
                  onChange={() => toggleAllFiltered(filtered)}
                  style={{ cursor:'pointer' }}
                />
              </th>
              <th>Order ID</th><th>Company</th><th>Vehicle</th><th>Specs</th><th>Files</th><th>Status</th><th>Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--text-3)', padding:60 }}>No orders match filters.</td></tr>
              : filtered.map(o => (
                <tr key={o.id} className="click" onClick={() => setSelOrder(o)} style={{ background: selectedOrders.has(o.id) ? 'var(--accent-dim)' : '' }}>
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedOrders.has(o.id)} onChange={() => toggleOne(o.id)} style={{ cursor:'pointer' }} />
                  </td>
                  <td style={{ color:'var(--text-2)', fontSize:11, fontFamily:"'Space Grotesk',sans-serif", letterSpacing:'1px' }}>{o.id}</td>
                  <td style={{ color:'var(--text-2)', fontSize:13 }}>{coOf(o.company_id)?.company || '—'}</td>
                  <td className="bc" style={{ fontWeight:700, fontSize:16, color:'#fff' }}>{o.vehicle}</td>
                  <td style={{ color:'var(--text-2)', fontSize:12 }}>{o.size}<br /><span style={{ color:'var(--text-3)' }}>{o.laminate} · Qty {o.quantity}</span></td>
                  <td>
                    <span style={{ color: o.files?.length ? 'var(--success)' : 'var(--warn)', fontSize:12, fontWeight:600 }}>
                      {o.files?.length || 0} file{o.files?.length !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td><StatusPill status={o.status} /></td>
                  <td style={{ color:'var(--text-2)', fontSize:12 }}>{o.created_at?.split('T')[0]}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (view === 'calendar') {
    const year  = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Build map: day → orders
    const ordersByDay = {};
    orders.forEach(o => {
      const d = new Date(o.created_at);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!ordersByDay[day]) ordersByDay[day] = [];
        ordersByDay[day].push(o);
      }
    });

    const DAYS_OF_WEEK = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const today = new Date();
    const isToday = (day) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

    // Build grid cells: leading empty cells + day cells
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const selectedDayOrders = calSelectedDay ? (ordersByDay[calSelectedDay] || []) : [];

    return wrap(
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
          <div className="bc" style={{ fontSize:32, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase' }}>Production Calendar</div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setCalMonth(m => new Date(m.getFullYear(), m.getMonth()-1, 1)); setCalSelectedDay(null); }}>‹ Prev</button>
            <span className="bc" style={{ fontSize:16, fontWeight:700, minWidth:160, textAlign:'center' }}>
              {calMonth.toLocaleString('default',{month:'long', year:'numeric'})}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => { setCalMonth(m => new Date(m.getFullYear(), m.getMonth()+1, 1)); setCalSelectedDay(null); }}>Next ›</button>
          </div>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:4 }}>
          {DAYS_OF_WEEK.map(d => (
            <div key={d} style={{ textAlign:'center', color:'var(--text-2)', fontSize:10, fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', padding:'8px 0', fontFamily:"'Space Grotesk',sans-serif" }}>{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:24 }}>
          {cells.map((day, idx) => {
            if (day === null) return <div key={`empty-${idx}`} />;
            const dayOrders = ordersByDay[day] || [];
            const isSelected = calSelectedDay === day;
            const isTodayCell = isToday(day);
            return (
              <div
                key={day}
                onClick={() => setCalSelectedDay(isSelected ? null : day)}
                style={{
                  background: isSelected ? 'var(--accent-dim)' : 'var(--bg-card)',
                  border: isSelected ? '1px solid rgba(0,159,255,0.4)' : isTodayCell ? '1px solid rgba(0,159,255,0.25)' : '1px solid var(--border)',
                  borderRadius:'var(--r-sm)',
                  padding:'8px',
                  minHeight:80,
                  cursor:'pointer',
                  transition:'all .15s',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background='var(--bg-elevated)'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background='var(--bg-card)'; }}
              >
                <div style={{
                  fontFamily:"'Space Grotesk',sans-serif",
                  fontWeight:700,
                  fontSize:14,
                  color: isTodayCell ? 'var(--accent)' : 'var(--text-2)',
                  marginBottom:4,
                }}>
                  {day}
                  {dayOrders.length > 0 && (
                    <span style={{ marginLeft:5, fontSize:10, color:'var(--accent)', fontWeight:700 }}>({dayOrders.length})</span>
                  )}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                  {dayOrders.slice(0,3).map(o => {
                    const sc = STATUS_CFG[o.status] || {};
                    return (
                      <div key={o.id} style={{ display:'flex', alignItems:'center', gap:4, overflow:'hidden' }}>
                        <div style={{ width:6, height:6, borderRadius:'50%', background:sc.color||'var(--text-3)', flexShrink:0 }} />
                        <span style={{ fontSize:10, color:'var(--text-2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {o.vehicle?.slice(0,12)}{o.vehicle?.length > 12 ? '…' : ''}
                        </span>
                      </div>
                    );
                  })}
                  {dayOrders.length > 3 && (
                    <div style={{ fontSize:10, color:'var(--text-3)' }}>+{dayOrders.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected day orders */}
        {calSelectedDay && (
          <div className="card">
            <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', background:'rgba(0,0,0,.15)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div className="bc" style={{ fontSize:15, fontWeight:800, letterSpacing:'2px', textTransform:'uppercase' }}>
                {calMonth.toLocaleString('default',{month:'long'})} {calSelectedDay} — {selectedDayOrders.length} Order{selectedDayOrders.length !== 1 ? 's' : ''}
              </div>
              <button onClick={() => setCalSelectedDay(null)} style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:18, cursor:'pointer' }}>×</button>
            </div>
            {selectedDayOrders.length === 0
              ? <div style={{ padding:'28px 20px', color:'var(--text-3)', fontSize:13 }}>No orders on this day.</div>
              : selectedDayOrders.map(o => (
                <div key={o.id} className="orow" onClick={() => setSelOrder(o)}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                    <div>
                      <div className="bc" style={{ fontWeight:700, fontSize:15, color:'#fff', marginBottom:3 }}>{o.vehicle}</div>
                      <div style={{ color:'var(--text-2)', fontSize:12 }}>{o.id} · {coOf(o.company_id)?.company} · {o.size}</div>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  }

  if (view === 'products') return wrap(
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div className="bc" style={{ fontSize:32, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase' }}>Products</div>
        <button className="btn btn-primary" onClick={() => setShowNewProd(true)}>+ Add Product</button>
      </div>
      <div className="card">
        <table>
          <thead>
            <tr><th>Name</th><th>Category</th><th>Base Price</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {products.length === 0
              ? <tr><td colSpan={5} style={{ textAlign:'center', color:'var(--text-3)', padding:60 }}>No products yet.</td></tr>
              : products.map(p => (
                <tr key={p.id} className="click" onClick={() => setEditProd(p)}>
                  <td className="bc" style={{ fontWeight:700, fontSize:16, color:'#fff' }}>{p.name}</td>
                  <td style={{ color:'var(--text-2)', fontSize:13 }}>{p.category || '—'}</td>
                  <td style={{ color:'var(--text-2)', fontWeight:600, fontFamily:"'Space Grotesk',sans-serif", fontSize:15 }}>
                    ${Number(p.base_price).toFixed(2)}
                  </td>
                  <td>
                    <span className="pill" style={{ background: p.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: p.is_active ? 'var(--success)' : 'var(--danger)' }}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditProd(p)}>Edit</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return wrap(
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div className="bc" style={{ fontSize:32, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase' }}>Partner Companies</div>
        <button className="btn btn-primary" onClick={() => setShowNewCo(true)}>+ Add Company</button>
      </div>
      <div className="card">
        <table>
          <thead>
            <tr><th>Company</th><th>Account #</th><th>Contact</th><th>Email</th><th>Orders</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {companies.length === 0
              ? <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--text-3)', padding:60 }}>No companies yet.</td></tr>
              : companies.map(co => (
                <tr key={co.id} className="click" onClick={() => setSelCompany(co)}>
                  <td className="bc" style={{ fontWeight:700, fontSize:16, color:'#fff' }}>{co.company}</td>
                  <td style={{ color:'var(--text-2)', fontSize:12, fontFamily:"'Space Grotesk',sans-serif", letterSpacing:'1px' }}>{co.account_num}</td>
                  <td style={{ color:'var(--text-2)', fontSize:13 }}>
                    {co.contact}<br /><span style={{ color:'var(--text-3)', fontSize:11 }}>{co.phone}</span>
                  </td>
                  <td style={{ color:'var(--text-2)', fontSize:12 }}>{co.email}</td>
                  <td style={{ color:'var(--text-2)', fontWeight:600 }}>{orders.filter(o=>o.company_id===co.id).length}</td>
                  <td>
                    <span className="pill" style={{ background: co.active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: co.active ? 'var(--success)' : 'var(--danger)' }}>
                      {co.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditCo(co)}>Edit</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── TRACKING PAGE (public) ───────────────────────────────────────────────────
function TrackingPage({ orderId }) {
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    supabase.from('orders').select('*, files:order_files(*)').eq('id', orderId).single()
      .then(({ data, error }) => {
        if (error || !data) setError('Order not found.');
        else setOrder(data);
        setLoading(false);
      });
  }, [orderId]);

  const sc = order ? (STATUS_CFG[order.status] || {}) : {};

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', color:'var(--text)' }}>
      <style>{CSS}</style>
      <div style={{ maxWidth:640, margin:'0 auto', padding:'48px 24px' }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <Logo height={44} />
          <div style={{ color:'var(--text-3)', fontSize:10, fontWeight:700, letterSpacing:'4px', textTransform:'uppercase', marginTop:10 }}>Order Tracking</div>
        </div>
        {loading && <div style={{ textAlign:'center', color:'var(--text-3)' }}>Loading…</div>}
        {error   && <div style={{ textAlign:'center', color:'var(--danger)', fontSize:14 }}>{error}</div>}
        {order && (
          <div className="card">
            <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div className="bc" style={{ fontSize:22, fontWeight:900, color:'#fff' }}>{order.vehicle}</div>
                <div style={{ color:'var(--text-2)', fontSize:12, marginTop:3 }}>{order.id} · {order.size}</div>
              </div>
              <span className="pill" style={{ background:sc.bg, color:sc.color, fontSize:13 }}>{sc.icon} {order.status}</span>
            </div>
            <div style={{ padding:'20px 24px' }}>
              {[
                ['Material', order.material],
                ['Laminate', order.laminate],
                ['Qty',      order.quantity],
                ['Status',   order.status],
                ...(order.tracking_number ? [['Tracking #', order.tracking_number]] : []),
              ].map(([l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border-sub)', fontSize:14 }}>
                  <span style={{ color:'var(--text-2)' }}>{l}</span>
                  <span style={{ fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>
            {order.tracking_number && (
              <div style={{ padding:'0 24px 20px' }}>
                <div style={{ background:'var(--accent-dim)', border:'1px solid rgba(0,159,255,0.2)', borderRadius:'var(--r-sm)', padding:'12px 16px', fontSize:13 }}>
                  📦 <strong>Tracking Number:</strong> {order.tracking_number}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [mode,    setMode]    = useState('login'); // 'login' | 'signup' | 'success'
  const [email,   setEmail]   = useState('');
  const [pw,      setPw]      = useState('');
  const [company, setCompany] = useState('');
  const [err,     setErr]     = useState('');
  const [loading, setLoading] = useState(false);

  const switchMode = (m) => { setMode(m); setErr(''); };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    const result = await onLogin(email.trim().toLowerCase(), pw);
    if (!result.ok) { setErr(result.error); setLoading(false); }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!company.trim()) { setErr('Company name is required.'); return; }
    if (pw.length < 8)   { setErr('Password must be at least 8 characters.'); return; }
    setLoading(true); setErr('');
    try {
      const { data, error: signUpErr } = await supabase.auth.signUp({
        email:    email.trim().toLowerCase(),
        password: pw,
      });
      if (signUpErr) throw signUpErr;
      // If email auto-confirmed, a session is returned and we can insert the company row.
      // If not (email confirm required), they'll log in after verifying and admin sees them in Auth.
      if (data.session || data.user) {
        await supabase.from('companies').insert({
          company: company.trim(),
          email:   email.trim().toLowerCase(),
          active:  true,
        });
      }
      setMode('success');
    } catch (e) {
      setErr(e.message || 'Sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bg = (
    <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
      <div className="login-grid" />
      <div className="login-glow" />
    </div>
  );

  const logoBlock = (
    <div style={{ textAlign:'center', marginBottom:36 }}>
      <Logo height={44} />
      <div style={{ color:'var(--text-3)', fontSize:9, fontWeight:600, letterSpacing:'3px', textTransform:'uppercase', marginTop:12, fontFamily:"'Inter',sans-serif" }}>
        Wholesale Partner Portal
      </div>
    </div>
  );

  const ErrBox = ({ msg }) => msg ? (
    <div style={{ color:'var(--danger)', fontSize:13, marginBottom:16, display:'flex', alignItems:'center', gap:7, background:'rgba(239,68,68,0.08)', padding:'10px 14px', borderRadius:'var(--r-sm)' }}>
      <span>⚠</span> {msg}
    </div>
  ) : null;

  if (mode === 'success') return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:20, position:'relative', overflow:'hidden' }}>
      <style>{CSS}</style>
      {bg}
      <div style={{ position:'relative', zIndex:1, width:'100%', maxWidth:420 }}>
        {logoBlock}
        <div className="card" style={{ borderTop:'2px solid var(--success)' }}>
          <div style={{ padding:36, textAlign:'center' }}>
            <div style={{ fontSize:44, marginBottom:14 }}>✅</div>
            <div className="bc" style={{ fontSize:22, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'var(--success)', marginBottom:8 }}>Account Created!</div>
            <div style={{ color:'var(--text-2)', fontSize:13, lineHeight:'1.7', marginBottom:28 }}>
              You can now sign in with your email and password.
              {' '}If you need to confirm your email first, check your inbox.
            </div>
            <button className="btn btn-primary" style={{ width:'100%' }} onClick={() => { switchMode('login'); }}>
              Sign In →
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:20, position:'relative', overflow:'hidden' }}>
      <style>{CSS}</style>
      {bg}

      <div style={{ position:'relative', zIndex:1, width:'100%', maxWidth:420 }}>
        {logoBlock}

        {mode === 'login' ? (
          <>
            <div className="card" style={{ borderTop:'2px solid var(--accent)' }}>
              <div style={{ padding:32 }}>
                <div className="bc" style={{ fontSize:22, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff', marginBottom:4 }}>Sign In</div>
                <div style={{ color:'var(--text-2)', fontSize:13, marginBottom:26 }}>Enter your partner credentials to continue.</div>
                <form onSubmit={submitLogin}>
                  <FInput label="Email Address" type="email" placeholder="you@yourcompany.com" value={email} onChange={e => setEmail(e.target.value)} required />
                  <FInput label="Password" type="password" placeholder="••••••••" value={pw} onChange={e => setPw(e.target.value)} required />
                  <ErrBox msg={err} />
                  <button className="btn btn-primary" type="submit" disabled={loading} style={{ width:'100%', padding:13, fontSize:14 }}>
                    {loading ? 'Signing in…' : 'Sign In →'}
                  </button>
                </form>
              </div>
            </div>
            <div style={{ marginTop:16, textAlign:'center' }}>
              <button
                onClick={() => switchMode('signup')}
                style={{ background:'none', border:'none', color:'var(--accent)', fontSize:13, cursor:'pointer', textDecoration:'underline', textDecorationColor:'rgba(0,159,255,0.3)' }}
              >
                New partner? Create an account →
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="card" style={{ borderTop:'2px solid var(--accent)' }}>
              <div style={{ padding:32 }}>
                <div className="bc" style={{ fontSize:22, fontWeight:900, letterSpacing:'1px', textTransform:'uppercase', color:'#fff', marginBottom:4 }}>Create Account</div>
                <div style={{ color:'var(--text-2)', fontSize:13, marginBottom:26 }}>Set up your wholesale partner account.</div>
                <form onSubmit={submitSignup}>
                  <FInput label="Company Name *" placeholder="e.g. Speed Graphics LLC" value={company} onChange={e => setCompany(e.target.value)} required />
                  <FInput label="Email Address *" type="email" placeholder="you@yourcompany.com" value={email} onChange={e => setEmail(e.target.value)} required />
                  <FInput label="Password *" type="password" placeholder="Min 8 characters" value={pw} onChange={e => setPw(e.target.value)} required />
                  <ErrBox msg={err} />
                  <button className="btn btn-primary" type="submit" disabled={loading} style={{ width:'100%', padding:13, fontSize:14 }}>
                    {loading ? 'Creating Account…' : 'Create Account →'}
                  </button>
                </form>
              </div>
            </div>
            <div style={{ marginTop:16, textAlign:'center' }}>
              <button
                onClick={() => switchMode('login')}
                style={{ background:'none', border:'none', color:'var(--text-2)', fontSize:13, cursor:'pointer' }}
              >
                Already have an account? <span style={{ color:'var(--accent)', textDecoration:'underline' }}>Sign in</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session,   setSession]   = useState(null);
  const [company,   setCompany]   = useState(null);
  const [isAdmin,   setIsAdmin]   = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [theme,     setTheme]     = useState(localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    // Safety-net: if nothing resolves within 6s, show login rather than hang forever
    const timeout = setTimeout(() => setAuthReady(true), 6000);

    const handleSession = async (sess) => {
      try {
        if (sess) {
          setSession(sess);
          const email = sess.user.email;
          if (email === ADMIN_EMAIL) {
            setIsAdmin(true);
            setCompany(null);
          } else {
            setIsAdmin(false);
            const { data } = await supabase.from('companies').select('*').eq('email', email).single();
            setCompany(data || null);
          }
        } else {
          setSession(null);
          setCompany(null);
          setIsAdmin(false);
        }
      } catch {
        // network error — still unblock the UI
      } finally {
        clearTimeout(timeout);
        setAuthReady(true);
      }
    };

    // Seed from stored session immediately (doesn't wait for token refresh)
    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));

    // Then keep in sync with any auth changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      handleSession(sess);
    });

    return () => { subscription.unsubscribe(); clearTimeout(timeout); };
  }, []);

  const login = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { ok:false, error:error.message };
    return { ok:true };
  };

  const logout = async () => { await supabase.auth.signOut(); };

  // Public order tracking via ?track=ORDER-ID (checked after all hooks)
  const trackId = new URLSearchParams(window.location.search).get('track');
  if (trackId) return <TrackingPage orderId={trackId} />;

  if (!authReady) return (
    <div style={{ minHeight:'100vh', background:'#09090E', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:18 }}>
      <style>{CSS}</style>
      <Logo height={38} />
      <div style={{ color:'#1D2535', fontSize:9, fontWeight:600, letterSpacing:'3px', textTransform:'uppercase', fontFamily:"'Inter',sans-serif" }}>Loading</div>
    </div>
  );

  if (!session) return <Login onLogin={login} />;
  if (isAdmin)  return <AdminPortal onLogout={logout} theme={theme} onThemeToggle={toggleTheme} />;
  if (company)  return <ClientPortal session={session} company={company} onLogout={logout} theme={theme} onThemeToggle={toggleTheme} />;

  return (
    <div style={{ minHeight:'100vh', background:'#09090E', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:14, color:'var(--text)', padding:20 }}>
      <style>{CSS}</style>
      <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>!</div>
      <div className="bc" style={{ fontSize:20, fontWeight:700, letterSpacing:'-0.3px' }}>Account Not Found</div>
      <div style={{ color:'var(--text-2)', fontSize:13, textAlign:'center', maxWidth:340, lineHeight:'1.6' }}>
        Your email isn't linked to a partner account.<br />Contact wholesale@ultimateprintingco.com
      </div>
      <button className="btn btn-ghost" onClick={logout} style={{ marginTop:8 }}>Sign Out</button>
    </div>
  );
}
